<template>
    <div class="page-gutter" style="margin-top: 10px; margin-bottom: 40px">
        <!-- IA reorg: the "Storalytic Intelligence Flow" bar is retired from the
             app (it duplicated the tab row and its orienting purpose is gone).
             Kept in the codebase for the sales deck only, per spec §5. -->

        <!-- IA reorg: the period + opening-hours picker moved OUT of the global
             chrome and INTO the analytical tabs (Explore/Zones) only — it drives
             their data. Actions + Today are a fixed 30-day basis and show no picker.
             See the conditional ControlHead just under the tab nav below. -->

        <!-- IA reorg: the above-tabs hero is fully retired. Today (the landing) owns
             Allen's narrative — including the store-trend prose the old Explore
             ReviewNarrativeCard used to carry (spec §3.1: the briefing merges those).
             The trend NUMBER lives in Explore's at-a-glance strip. -->

        <!-- Tabs -->
        <MDBTabs v-model="activeTab">
            <MDBTabNav tabsClasses="mb-3 stora-tabs-inkbar" style="padding-top: 20px;">
                <!-- IA reorg: Actions · Zones · Explore — a drill-down flow (what to do
                     → why/where → raw numbers). Actions is the default; Explore is the
                     de-emphasised reference surface (spec §2). Today is the landing (home).
                     No tier gating — everyone gets everything. -->
                <MDBTabItem tabId="actions-layer">
                    <i class="bi bi-list-check tab-icon"></i><span>{{ $t('demoDashboard.tabs.actions') }}</span>
                </MDBTabItem>

                <MDBTabItem tabId="intelligence-layer">
                    <i class="bi bi-grid-3x3-gap tab-icon"></i><span>{{ $t('demoDashboard.tabs.zones') }}</span>
                </MDBTabItem>

                <MDBTabItem tabId="data-foundation-layer">
                    <i class="bi bi-compass tab-icon"></i><span>{{ $t('demoDashboard.tabs.explore') }}</span>
                </MDBTabItem>
            </MDBTabNav>

            <!-- Shared analytical picker — shown ONLY on Explore + Zones (the tabs
                 whose data it drives). Hidden on Actions (fixed 30-day basis). -->
            <ControlHead
                v-if="activeTab === 'data-foundation-layer' || activeTab === 'intelligence-layer'"
                class="in-tab-controls"
            >
                <duration-dropdown
                    :dateRangeMode="dateRangeMode"
                    :selectedDuration="selectedDuration"
                    :disabledDurations="disabledDurations"
                >
                </duration-dropdown>
                <dropdown-opening-hours-mode
                    :openingHoursMode="selectedOpeningHoursMode"
                    :disabledModes="disabledOpeningHourModes"
                >
                </dropdown-opening-hours-mode>
            </ControlHead>
            <!-- Zone identity 6.7: the opening hours were changed today. -->
            <ChangeNotice
                v-if="(activeTab === 'data-foundation-layer' || activeTab === 'intelligence-layer')
                      && settingChanged(registerFor($route.query), 'opening_hours')"
                :compact="true" :text="NOTICES.settings.opening_hours" />

            <MDBTabContent>

                <!-- PRIORITIZATION -->
                <!-- Prioritization pane retired — the deep analytical cards move to
                     Zones (per-zone L1/L2/L3); the ranked opportunities now open the
                     Actions lifecycle. Gauges live on Today's "Store health". -->

                <!-- ZONES (was Intelligence) — cross-zone map + per-zone deep dive. -->
                <MDBTabPane tabId="intelligence-layer">
                    <!-- 6.7: the € in the Zones tab follow a money-settings change tonight. -->
                    <ChangeNotice v-if="activeTab === 'intelligence-layer' && settingChanged(registerFor($route.query), 'money')"
                        :compact="true" :text="NOTICES.settings.money" />
                    <!-- Weather signature (the "why"): a compact tile linking into the
                         full Explore Weather sub-tab. Outside group-disabled so it never greys. -->
                    <storalytic-demo-weather-signature
                        v-if="activeTab === 'intelligence-layer'"
                        @open="activeTab = 'data-foundation-layer'; exploreSubTab = 'sec-weather'"
                    />
                    <div v-if="activeTab==='intelligence-layer'" :class="{ 'group-disabled': !isProductZonePresent() }">
                        <div class="card-container-product-intelligence">
                            <!-- Zones overview: the strategic-positioning quadrant (the
                                 cross-zone map) on the left, and a role KEY on the right
                                 that fills the space + makes the map actionable. Pattern
                                 matrix dropped (its signal is on the zone cards). -->
                            <div class="zones-overview">
                                <div class="zov-chart">
                                    <StrategicPositioningChart
                                        :fixed-widgets-data="fixedWidgetsData"
                                        :is-active="activeTab === 'intelligence-layer'"
                                        :segment-key="companySector"
                                        :zone-overrides="zoneOverrides"
                                        :hide-legend="true"
                                    />
                                </div>
                                <aside class="zov-roles">
                                    <div class="zov-roles-title">{{ $t('demoDashboard.roles.title') }}</div>
                                    <div v-for="r in zoneRolesGrouped" :key="r.label" class="role-group">
                                        <div class="role-head" :style="{ color: r.color }">
                                            <span class="role-ico">{{ r.icon }}</span>{{ r.label }}
                                            <span class="role-count">{{ r.zones.length }}</span>
                                        </div>
                                        <div class="role-meaning">{{ r.meaning }}</div>
                                        <div class="role-zones">
                                            <button
                                                v-for="z in r.zones"
                                                :key="z.name"
                                                type="button"
                                                class="role-zonerow"
                                                :class="{ active: (zoneFocus || heroFocusedZone) === z.name }"
                                                :style="{ borderLeftColor: r.color }"
                                                @click="zoneFocus = z.name"
                                            >
                                                <span class="rz-top">
                                                    <span class="rz-name">{{ z.name }}</span>
                                                    <span v-if="z.eurText" class="rz-eur">{{ z.eurText }}</span>
                                                    <i class="bi bi-chevron-right rz-caret"></i>
                                                </span>
                                                <span class="rz-pattern" :style="{ background: z.patternBg, color: z.patternColor }">{{ z.patternIcon }} {{ z.patternChip }}</span>
                                                <span class="rz-stat">{{ $t('demoDashboard.roles.stat', { eng: z.engPct, conv: z.convPct, visitors: fmt(z.visitors) }) }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </aside>
                            </div>

                            <!-- Funnel only — the role cards on the right are the zone
                                 browser (they carry the grid's info now), so the grid is
                                 retired. Clicking a role card sets zoneFocus → this funnel. -->
                            <product-zone-deep-dive
                                browser-mode="none"
                                :priorities="topPriorities"
                                :store-total-visitors="storeTotalVisitors"
                                :focused-zone="zoneFocus || heroFocusedZone"
                                :segment-key="companySector"
                                :zone-overrides="zoneOverrides"
                            />
                        </div>
                    </div>
                </MDBTabPane>
                
                <!-- DATA FOUNDATION -->
                <MDBTabPane tabId="data-foundation-layer">
                    <div v-if="activeTab==='data-foundation-layer'" class="data-foundation-pane">

                        <!-- Explore's at-a-glance strip IS the intro — no redundant "Explore"
                             title (the tab already names it) and no explainer prose (the jump-nav
                             + collapsible sections speak for themselves). One headline per PRESENT
                             domain, so it summarises the whole tab; Product/Service are comparison
                             domains whose headline is the in-section chart, not a scalar. -->
                        <div class="explore-glance" :aria-label="$t('demoDashboard.glance.aria')">
                                <div v-if="isEntranceZonePresent() || isCombinedEntranceExitZonePresent()" class="eg-tile">
                                    <span class="eg-val">{{ fmt(storeTotalVisitors) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.visitors') }}</span>
                                </div>
                                <div v-if="(isEntranceZonePresent() || isCombinedEntranceExitZonePresent()) && averageVisitDuration" class="eg-tile">
                                    <span class="eg-val">{{ formattedDurationTime(averageVisitDuration) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.visitLength') }}</span>
                                </div>
                                <div v-if="(isEntranceZonePresent() || isCombinedEntranceExitZonePresent()) && formattedBusiestHour" class="eg-tile">
                                    <span class="eg-val">{{ formattedBusiestHour }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.busiestHour') }}</span>
                                </div>
                                <div v-if="isCheckoutWaitingZonePresent() && mostTypicalWaitingTime" class="eg-tile">
                                    <span class="eg-val">{{ formattedDurationTime(mostTypicalWaitingTime) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.typicalWait') }}</span>
                                </div>
                                <div v-if="isCheckoutInteractionZonePresent() && mostTypicalInteractionTime" class="eg-tile">
                                    <span class="eg-val">{{ formattedDurationTime(mostTypicalInteractionTime) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.typicalCheckout') }}</span>
                                </div>
                                <div v-if="checkoutUnattendedRate" class="eg-tile">
                                    <span class="eg-val">{{ $t('demoDashboard.glance.oneIn', { n: checkoutUnattendedRate }) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.emptyCheckout') }}</span>
                                </div>
                                <!-- Product-domain store stats (spec §5: €/visitor, %-engaged → Explore) -->
                                <div v-if="exploreEurPerVisitor != null" class="eg-tile">
                                    <span class="eg-val">{{ eur2(exploreEurPerVisitor) }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.eurPerVisitor') }}</span>
                                </div>
                                <div v-if="exploreEngagedPct != null" class="eg-tile">
                                    <span class="eg-val">{{ Math.round(exploreEngagedPct * 100) }}%</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.engaged') }}</span>
                                </div>
                                <div v-if="trendInfo" class="eg-tile">
                                    <span class="eg-val" :style="{ color: trendInfo.color || '#2b3a46' }">{{ trendInfo.short }}</span>
                                    <span class="eg-lbl">{{ $t('demoDashboard.glance.visitorTrend') }}</span>
                                </div>
                            </div>

                        <ZoneTypeNav
                            :items="zoneNavItems"
                            :active="exploreSubTab"
                            @navigate="exploreSubTab = $event"
                        />

                        <!-- Small title for the active sub-tab so the charts aren't naked
                             (the section headers themselves are hidden — see :hide-head). -->
                        <h3 v-if="activeZoneItem" class="explore-subtitle">
                            <i :class="['bi', activeZoneItem.icon]" :style="{ color: activeZoneItem.accent }"></i>
                            {{ activeZoneItem.title }}
                        </h3>

                        <!-- Weather deep-dive — its own Explore sub-tab (last). The store
                             signature + per-zone signals; Home/Intelligence point in here. -->
                        <Section v-show="exploreSubTab === 'sec-weather'" :hide-head="true" margin-top="6px">
                            <storalytic-demo-weather-card />
                        </Section>

                        <Section
                            id="sec-store-visitors"
                            v-show="exploreSubTab === 'sec-store-visitors'"
                            :title="$t('demoDashboard.sections.storeVisitors')"
                            icon="bi-shop"
                                                       accent="var(--stora-green)"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.4 (?scenario=type_change_entrance). -->
                            <ChangeNotice v-if="entranceTypeChange(registerFor($route.query))"
                                :text="NOTICES.storeValues" />
                            <template #summary>
                                <span v-if="zoneStats.entrance.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', zoneStats.entrance.polygon) }}
                                </span>
                                <span v-if="zoneStats.entrance.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', zoneStats.entrance.tripwire) }}
                                </span>
                            </template>

                            <div
                                v-if="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                class="section-empty-state"
                            >
                                <i class="bi bi-door-open empty-ico"></i>
                                <div class="empty-text">
                                    <strong>{{ $t('demoDashboard.store.noEntrance') }}</strong>
                                    <span>{{ $t('demoDashboard.store.noEntranceHint') }}</span>
                                </div>
                            </div>

                            <template v-else>
                            <ChartGrid>
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.weekdayHour')"
                                    icon="bi-grid-3x3-gap"
                                    :height="400"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!heatmapData?.heatmap_matrix || !heatmapData.heatmap_matrix.flat().some(v => v > 0)"
                                    accent="#36910d"
                                >
                                    <heatmap-dashboard
                                    :heatmapData="heatmapData"
                                    :selectedDuration="selectedDuration"
                                    :is-active="activeTab === 'data-foundation-layer'"
                                    :height="340"
                                    />
                                </ChartPanel>

                                <ChartPanel
                                    :title="$t('demoDashboard.charts.visitorsTrend')"
                                    icon="bi-activity"
                                    :height="400"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!entranceChartData || !Object.keys(entranceChartData).length"
                                    accent="#36910d"
                                >
                                    <charts-dashboard-entrance
                                    :chartData="entranceChartData"
                                    :selectedDuration="selectedDuration"
                                    :is-active="activeTab === 'data-foundation-layer'"
                                    :height="340"
                                    />
                                </ChartPanel>
                            </ChartGrid>

                            <KpiGrid style="padding-top:20px">
                                <!-- Total Visitors -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.totalVisitors')"
                                    :value="fmt(storeTotalVisitors)"
                                    icon="bi-people"
                                    accent="#36910d"
                                    :loading="false"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(storeTotalVisitors)"
                                >
                                    <template #badge>
                                        <span v-if="storeDataIntegrity"
                                              class="integrity-badge"
                                              :title="$t('demoDashboard.store.integrityTitle', { detail: storeDataIntegrity.detail })">
                                            <i class="bi bi-exclamation-triangle-fill"></i> {{ $t('demoDashboard.store.incomplete') }}
                                        </span>
                                    </template>
                                </KpiCard>

                                <!-- Avg / Day -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.avgPerDay')"
                                    :value="fmt(averageValue)"
                                    :suffix="$t('demoDashboard.store.avgSuffix')"
                                    icon="bi-graph-up"
                                    accent="#36910d"
                                    :loading="isLoadingAverage"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageValue)"
                                >
                                    <template #meta>{{ $t('demoDashboard.store.overDays', effectiveDurationDays) }}</template>
                                </KpiCard>

                                <!-- <Weekday>'s Average Visitors -->
                                <KpiCard
                                    :label="$t('demoDashboard.store.weekdayAvg', { day: currentWeekdayString })"
                                    :value="fmt(averageValueSameWeekday)"
                                    icon="bi-calendar-week"
                                    accent="#36910d"
                                    :loading="isLoadingAverageSameWeekday"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageValueSameWeekday)"
                                />

                                <!-- Visitor Trend (custom value slot, but same typography) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.visitorTrend')"
                                    :icon="trendInfo?.icon || 'bi-activity'"
                                    :accent="trendInfo?.color || '#999'"
                                    size="sm"
                                    :loading="isLoadingTrend"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!trendInfo"
                                >
                                    <!-- single bold word, smaller -->
                                    <template #value>
                                        <span class="trend-short">{{ trendInfo?.short }}</span>
                                    </template>

                                    <!-- subdued, one line if possible -->
                                    <template #meta>
                                        <span class="trend-sentence">{{ trendInfo?.sentence }}</span>
                                    </template>
                                </KpiCard>

                                <!-- Busiest Hour (avg) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.busiestHourAvg')"
                                    :value="formattedBusiestHour"
                                    icon="bi-clock-history"
                                    accent="#36910d"
                                    :loading="isLoadingBusiestHour"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(busiestHour)"
                                    :muted="!highestAverageHourlyCount"
                                >
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(highestAverageHourlyCount) }) }}</template>
                                </KpiCard>

                                <!-- Top Day -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.bestDay')"
                                    :value="formattedHighestTotalDay?.formattedDate"
                                    icon="bi-calendar3"
                                    accent="#36910d"
                                    :loading="isLoadingHighestDay"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(formattedHighestTotalDay?.formattedDate)"
                                >
                                    <template #meta>
                                        <i18n-t keypath="demoDashboard.unit.visitors" tag="span" scope="global"><template #n><strong>{{ fmt(highestTotalDayCount) }}</strong></template></i18n-t>
                                        <span v-if="bestDayLiftPct != null" class="best-day-lift">
                                            {{ $t('demoDashboard.store.lift', { pct: (bestDayLiftPct >= 0 ? '+' : '') + bestDayLiftPct }) }}
                                        </span>
                                    </template>
                                </KpiCard>

                                <!-- Top Visitor Hour -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.topVisitorHour')"
                                    icon="bi-clock"
                                    accent="#36910d"
                                    :loading="isLoadingHighestHour"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(formattedHighestTotalHour?.formattedDate)"
                                >
                                    <template #value>
                                    {{ formattedHighestTotalHour?.formattedDate }} | {{ formattedHighestTotalHour?.formattedTime }}
                                    </template>
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(highestTotalHourCount) }) }}</template>
                                </KpiCard>

                                <!-- Average Visit Duration (uses same states; custom empty text when exits missing) -->
                                <!-- value via slot for full control -->
                                <KpiCard
                                    v-if="!isCardEmpty(averageVisitDuration)"
                                    :label="$t('demoDashboard.kpi.avgVisitDuration')"
                                    icon="bi-stopwatch"
                                    accent="#36910d"
                                    :value="''"
                                    :loading="isLoadingleastUsedCheckout"
                                    :noZones="!isExitZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageVisitDuration)"
                                    :noZonesText="$t('demoDashboard.noZones.exit')"
                                >
                                    <template #value>
                                    <!-- If you have a formatted duration, show it here; otherwise fallback -->
                                    <span v-if="averageVisitDuration">
                                        {{ formattedDurationTime(averageVisitDuration) }}
                                    </span>
                                    <span v-else>{{ $t('demoDashboard.store.noExitCamera') }}</span>
                                    </template>
                                </KpiCard>

                                <!-- Est. Average Store Dwell — single Combined EE tripwire only.
                                     Production gates this on having exactly one active entry/exit
                                     zone that is a Combined EE with tripwires and UseTripwireForDwell=true.
                                     In the demo, the synthetic store-level dwell fields back it. -->
                                <KpiCard
                                    v-if="storeDwellEstimateSeconds > 0"
                                    :label="$t('demoDashboard.kpi.storeVisitLengthEst')"
                                    icon="bi-stopwatch"
                                    accent="#36910d"
                                    tripwire
                                >
                                    <template #value>
                                        {{ formattedDurationTime(storeDwellEstimateSeconds) }}
                                        <span v-if="storeDwellConfidence"
                                              :class="['conf-chip', `conf-${storeDwellConfidence}`]">{{ confLabel(storeDwellConfidence) }}</span>
                                        <span v-if="biasChipForValue(storeDwellBiasPct)"
                                              :class="biasChipForValue(storeDwellBiasPct).cls"
>{{ biasChipForValue(storeDwellBiasPct).label }}</span>
                                    </template>
                                    <template #meta>{{ $t('demoDashboard.store.estimatedFrom') }}</template>
                                </KpiCard>
                            </KpiGrid>
                            </template>
                        </Section>

                        <!-- Checkout Waiting -->
                        <Section
                            id="sec-checkout-waiting"
                            v-show="exploreSubTab === 'sec-checkout-waiting'"
                            :title="$t('demoDashboard.sections.checkoutWaiting')"
                            icon="bi-hourglass-split"
                                                       accent="#AD3935"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <template #summary>
                                <span v-if="zoneStats.waiting.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', zoneStats.waiting.polygon) }}
                                </span>
                                <span v-if="zoneStats.waiting.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', zoneStats.waiting.tripwire) }}
                                </span>
                                <span v-if="!zoneStats.waiting.polygon && !zoneStats.waiting.tripwire">{{ $t('demoDashboard.summary.noWaitingZones') }}</span>
                            </template>
                            <KpiGrid style="padding-top:20px">
                                <!-- Checkout Queue — leads the section -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.checkoutQueue')"
                                    icon="bi-people"
                                    accent="#AD3935"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="!checkoutQueue"
                                >
                                    <template #value>{{ queueVerdict }}</template>
                                    <template #meta>{{ $t('demoDashboard.kpi.queueMeta', { pct: Math.round(queuePct) }) }}</template>
                                </KpiCard>

                                <!-- Average Waiting Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.avgWaitingTime')"
                                    icon="bi-stopwatch"
                                    accent="#AD3935"
                                    :loading="isLoadingaverageWaitingTime"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(averageWaitingTime)"
                                >
                                    <template #value>{{ averageWaitingTime ? formattedDurationTime(averageWaitingTime) : '' }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.realWaits', { n: realWaitCount }) }}</template>
                                </KpiCard>

                                <!-- Typical Waiting Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.typicalWaitingTime')"
                                    icon="bi-activity"
                                    accent="#AD3935"
                                    :loading="isLoadingmostTypicalWaitingTime"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(mostTypicalWaitingTime)"
                                >
                                    <template #value>
                                        {{ mostTypicalWaitingTime ? formattedDurationTime(mostTypicalWaitingTime) : '' }}
                                    </template>
                                    <template #meta>
                                        {{ $t('demoDashboard.unit.realWaits', { n: realWaitCount }) }}
                                    </template>
                                </KpiCard>

                                <!-- Highest Waiting Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.highestWaitingTime')"
                                    icon="bi-graph-up-arrow"
                                    accent="#AD3935"
                                    :loading="isLoadingmaxWaitingDuration"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(maxWaitingDuration)"
                                >
                                    <template #value>
                                        {{ maxWaitingDuration ? formattedDurationTime(maxWaitingDuration) : '' }}
                                    </template>
                                    <template #meta>
                                        {{ $t('demoDashboard.unit.at', { zone: maxWaitingZoneName }) }}
                                    </template>
                                </KpiCard>

                                <!-- Real Wait Rate -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.pausedAtCheckout')"
                                    icon="bi-people"
                                    accent="#AD3935"
                                    :loading="isLoadingRealWaitRate"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(realWaitRate)"
                                >
                                    <template #value>{{ pctVal(realWaitRate) }}</template>
                                    <template #meta>{{ $t('demoDashboard.kpi.pausedMeta') }}</template>
                                </KpiCard>

                                <!-- Average Time in Zone -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.avgTimeInZone')"
                                    icon="bi-clock-history"
                                    :value="averageTimeInZoneWaiting ? formattedDurationTime(averageTimeInZoneWaiting) : ''"
                                    accent="#AD3935"
                                    :loading="isLoadingaverageTimeInZoneWaiting"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(averageTimeInZoneWaiting)"
                                />

                                <!-- Fastest Waiting Line -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.fastestWaiting')"
                                    icon="bi-speedometer2"
                                    accent="#AD3935"
                                    :loading="isLoadingfastestCheckoutZone"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(fastestCheckoutZone)"
                                >
                                    <template #value>
                                        {{ fastestCheckoutZone }}
                                    </template>
                                </KpiCard>

                                <!-- Slowest Waiting Line -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.slowestWaiting')"
                                    icon="bi-hourglass-split"
                                    accent="#AD3935"
                                    :loading="isLoadingslowestCheckoutZone"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutWaiting')"
                                    :empty="isCardEmpty(slowestCheckoutZone)"
                                >
                                    <template #value>
                                        {{ slowestCheckoutZone }}
                                    </template>
                                </KpiCard>
                            </KpiGrid>
                        </Section>

                        <!-- Checkout Interaction -->
                        <Section
                            id="sec-checkout-interaction"
                            v-show="exploreSubTab === 'sec-checkout-interaction'"
                            :title="$t('demoDashboard.sections.checkoutInteraction')"
                            icon="bi-credit-card-2-front"
                                                       accent="#D3A741"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([5, 999])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.interaction.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', zoneStats.interaction.polygon) }}
                                </span>
                                <span v-if="zoneStats.interaction.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', zoneStats.interaction.tripwire) }}
                                </span>
                                <span v-if="!zoneStats.interaction.polygon && !zoneStats.interaction.tripwire">{{ $t('demoDashboard.summary.noInteractionZones') }}</span>
                            </template>
                            <KpiGrid style="padding-top:20px">
                                <!-- Average Interaction Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.avgInteractionTime')"
                                    icon="bi-stopwatch"
                                    accent="#D3A741"
                                    :loading="isLoadingaverageInteractionTime"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(averageInteractionTime)"
                                >
                                    <template #value>{{ averageInteractionTime ? formattedDurationTime(averageInteractionTime) : '' }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.realTransactions', { n: realTransactionCount }) }}</template>
                                </KpiCard>

                                <!-- Typical Interaction Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.typicalInteractionTime')"
                                    icon="bi-activity"
                                    accent="#D3A741"
                                    :loading="isLoadingmostTypicalInteractionTime"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(mostTypicalInteractionTime)"
                                >
                                    <template #value>{{ mostTypicalInteractionTime ? formattedDurationTime(mostTypicalInteractionTime) : '' }}</template>
                                    <template #meta>
                                        {{ $t('demoDashboard.unit.realTransactions', { n: realTransactionCount }) }}
                                    </template>
                                </KpiCard>

                                <!-- Highest Interaction Time -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.highestInteractionTime')"
                                    icon="bi-graph-up-arrow"
                                    accent="#D3A741"
                                    :loading="isLoadingmaxInteractionDuration"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(maxInteractionDuration)"
                                >
                                    <template #value>{{ maxInteractionDuration ? formattedDurationTime(maxInteractionDuration) : '' }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.at', { zone: maxInteractionZoneName }) }}</template>
                                </KpiCard>

                                <!-- Real Transaction Rate -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.realTransactionRate')"
                                    icon="bi-people"
                                    accent="#D3A741"
                                    :loading="isLoadingRealTransactionRate"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(realTransactionRate)"
                                >
                                    <template #value>{{ pctVal(realTransactionRate) }}</template>
                                    <template #meta>{{ $t('demoDashboard.kpi.transactedMeta') }}</template>
                                </KpiCard>

                                <!-- Counter unattended (docs §28.6): customers who reached a counter
                                     with nobody behind it. Sits with the other outcome figures rather
                                     than with the durations — it is a count of people, not a time. -->
                                <CounterUnattendedCard
                                    v-if="countersByService.checkout.length"
                                    :counters="countersByService.checkout"
                                    accent="#D3A741"
                                />

                                <!-- Average Time in Zone -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.avgTimeInZone')"
                                    icon="bi-clock-history"
                                    :value="averageTimeInZoneInteraction ? formattedDurationTime(averageTimeInZoneInteraction) : ''"
                                    accent="#D3A741"
                                    :loading="isLoadingaverageTimeInZoneInteraction"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(averageTimeInZoneInteraction)"
                                />

                                <!-- Fastest Interaction Checkout -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.fastestInteraction')"
                                    icon="bi-speedometer2"
                                    accent="#D3A741"
                                    :loading="isLoadingfastestInteractionZone"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(fastestInteractionZone)"
                                >
                                    <template #value>{{ fastestInteractionZone }}</template>
                                </KpiCard>

                                <!-- Slowest Interaction Checkout -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.slowestInteraction')"
                                    icon="bi-hourglass-split"
                                    accent="#D3A741"
                                    :loading="isLoadingslowestInteractionZone"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(slowestInteractionZone)"
                                >
                                    <template #value>{{ slowestInteractionZone }}</template>
                                </KpiCard>
                            
                                <!-- Most Used Checkout -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.mostUsedCheckout')"
                                    icon="bi-check2-circle"
                                    accent="#D3A741"
                                    :loading="isLoadingmostUsedCheckout"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(mostUsedCheckout?.zone_name)"
                                >
                                    <template #value>{{ mostUsedCheckout?.zone_name }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.interactions', { n: fmt(mostUsedCheckout?.visitor_count) }) }}</template>
                                </KpiCard>

                                <!-- Least Used Checkout -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.leastUsedCheckout')"
                                    icon="bi-dash-circle"
                                    accent="#D3A741"
                                    :loading="isLoadingleastUsedCheckout"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.checkoutInteraction')"
                                    :empty="isCardEmpty(leastUsedCheckout?.zone_name)"
                                >
                                    <template #value>{{ leastUsedCheckout?.zone_name }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.interactions', { n: fmt(leastUsedCheckout?.visitor_count) }) }}</template>
                                </KpiCard>
                            </KpiGrid>
                            <!-- Throughput — real transactions per till, and when each
                                 till is actually working. "Real" is the pipeline's own
                                 definition (clear lingerers at the interaction zone), not
                                 a raw visit count: a few seconds at a till is somebody
                                 walking past, not somebody paying. -->
                            <CheckoutTransactionsPanel
                              :data="checkoutTransactions"
                              accent="#D3A741"
                            />


                            <!-- Counter unattended (docs §28.6). Info and
                                 Pick Up stay greyed teasers by design — the
                                 demo withholds data by phase, not by capability. -->
                            <CounterUnattendedPanel
                                v-if="countersByService.checkout.length"
                                :counters="countersByService.checkout"
                                accent="#D3A741"
                            />
                        </Section>

                        <!-- Service Zones — tripwire-only Customer Service Desk in the demo -->
                        <Section
                            v-if="showServiceSection"
                            id="sec-service"
                            v-show="exploreSubTab === 'sec-service'"
                            :title="$t('demoDashboard.sections.service')"
                            icon="bi-headset"
                                                       accent="#D97706"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([10, 13, 16])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.service.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', zoneStats.service.polygon) }}
                                </span>
                                <span v-if="zoneStats.service.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', zoneStats.service.tripwire) }}
                                </span>
                            </template>

                            <!-- ========================= POLYGON SUB-PANEL =========================
                                 Hidden in the demo (no polygon service zones). Kept structurally
                                 identical to production so a future polygon service zone needs
                                 only data to render. -->
                            <SubPanel
                                v-if="hasPolygonServiceZones"
                                :label="$t('demoDashboard.geometry.polygon')"
                                :countText="$t('demoDashboard.count.zones', zoneStats.service.polygon)"
                                icon="bi-bounding-box"
                                accent="#D97706"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('demoDashboard.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#D97706"
                                        :empty="!(bestServiceZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestServiceZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.avg', { d: formattedDurationTime(bestServiceZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('demoDashboard.kpi.topVisitors')" icon="bi-people" accent="#D97706"
                                        :empty="!(bestServiceZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestServiceZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(bestServiceZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>
                                    <!-- Counter unattended (docs §28.6): customers who reached a counter
                                         with nobody behind it. With the other outcome figures, ahead of the
                                         small coverage cards. -->
                                    <CounterUnattendedCard
                                        v-if="countersByService.service.length"
                                        :counters="countersByService.service"
                                        accent="#D97706"
                                    />
                                    <KpiCard :label="$t('demoDashboard.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#D97706" size="sm"
                                        :empty="(zoneCoverageTextServiceZone || '').startsWith('0/0')">
                                        <template #value>{{ zoneCoverageTextServiceZone }}</template>
                                    </KpiCard>
                                </KpiGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL =========================
                                 Renders for the Customer Service Desk (tripwire-only). Mode C
                                 estimates back the Top Avg. Duration KPI + Estimated Avg/Total
                                 Time barlists, with confidence + bias chips. -->
                            <SubPanel
                                v-if="hasTripwireServiceZones"
                                :label="$t('demoDashboard.geometry.tripwire')"
                                :countText="$t('demoDashboard.count.zones', zoneStats.service.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#D97706"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('demoDashboard.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#D97706"
                                        :empty="!(bestServiceZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestServiceZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.crossings', { n: fmt(bestServiceZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('demoDashboard.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#D97706"
                                        :empty="!(lowestServiceZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestServiceZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.crossings', { n: fmt(lowestServiceZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('demoDashboard.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#D97706" size="sm"
                                        :empty="!crossingsByServiceZone.length">
                                        <template #value>{{ crossingCoverageTextServiceZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('demoDashboard.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#D97706" size="sm"
                                        :empty="!zeroCrossingZonesServiceZone.length">
                                        <template #value>{{ zeroCrossingZonesServiceZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesServiceZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                    <KpiCard v-if="hasEstimatedServiceZones" :label="$t('demoDashboard.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#D97706"
                                        :empty="!(bestServiceZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestServiceZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]}`]"
>{{ confLabel(estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestServiceZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('demoDashboard.charts.crossingsService')" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByServiceZone?.some(r => r.crossings > 0)" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByServiceZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedServiceZones" :title="$t('demoDashboard.charts.estAvgDwellService')" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByServiceZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.service[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.service[row.zone]}`]">{{ confLabel(estDwellConfidence.service[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('service', row.zone)" :class="biasChipFor('service', row.zone).cls">{{ biasChipFor('service', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedServiceZones" :title="$t('demoDashboard.charts.estTotalService')" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByServiceZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.service[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.service[row.zone]}`]">{{ confLabel(estDwellConfidence.service[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('service', row.zone)" :class="biasChipFor('service', row.zone).cls">{{ biasChipFor('service', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- Counter unattended (docs §28.6). Info and
                                 Pick Up stay greyed teasers by design — the
                                 demo withholds data by phase, not by capability. -->
                            <CounterUnattendedPanel
                                v-if="countersByService.service.length"
                                :counters="countersByService.service"
                                accent="#D97706"
                            />
                        </Section>

                        <!-- Product Zones -->
                        <Section
                            v-if="showProductSection"
                            id="sec-product"
                            v-show="exploreSubTab === 'sec-product'"
                            :title="$t('demoDashboard.sections.product')"
                            icon="bi-tag"
                                                       accent="#004F6E"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <template #summary>
                                <span v-if="zoneStats.product.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', zoneStats.product.polygon) }}
                                </span>
                                <span v-if="zoneStats.product.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', zoneStats.product.tripwire) }}
                                </span>
                            </template>

                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonProductZones"
                                :label="$t('demoDashboard.geometry.polygon')"
                                :countText="$t('demoDashboard.count.zones', zoneStats.product.polygon)"
                                icon="bi-bounding-box"
                                accent="#004F6E"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('demoDashboard.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#004F6E"
                                        :loading="isLoadingbestProductZoneInAvgDuration"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('demoDashboard.noZones.product')"
                                        :empty="!(bestProductZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestProductZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.avg', { d: formattedDurationTime(bestProductZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.topVisitors')" icon="bi-people" accent="#004F6E"
                                        :loading="isLoadingbestProductZoneInCounts"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('demoDashboard.noZones.product')"
                                        :empty="!(bestProductZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestProductZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(bestProductZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.lowestVisitors')" icon="bi-arrow-down" accent="#004F6E"
                                        :loading="isLoadingworstProductZoneInCounts"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('demoDashboard.noZones.product')"
                                        :empty="!(worstVisitorsFromRowsProductZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsProductZone?.zone }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(worstVisitorsFromRowsProductZone?.visitors) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.mostClearLingerers')" icon="bi-person-check" accent="#004F6E"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('demoDashboard.noZones.product')"
                                        :empty="!(topClearLingerersProductZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersProductZone?.zone }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.clearLingerers', { n: fmt(topClearLingerersProductZone?.clears) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#004F6E"
                                        :noZones="!isProductZonePresent()"
                                        :empty="highestEngagementZoneProductZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneProductZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneProductZone.clears) }} / {{ fmt(highestEngagementZoneProductZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneProductZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.topEngagementPotential')" icon="bi-cash-coin" accent="#004F6E"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('demoDashboard.noZones.product')"
                                        :empty="!(topSalesPotentialProductZone?.engSales > 0)"
                                        :emptyText="$t('demoDashboard.kpi.noEngagementPotential')">
                                        <template #value>{{ topSalesPotentialProductZone?.zone }}</template>
                                        <template #meta>{{ asMoney(topSalesPotentialProductZone?.engSales) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#004F6E" size="sm"
                                        :noZones="!isProductZonePresent()"
                                        :empty="productZoneNames.length === 0">
                                        <template #value>{{ zoneCoverageTextProductZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.zeroVisitorZones')" icon="bi-x-circle" accent="#004F6E" size="sm"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!zeroVisitorZonesProductZone.length">
                                        <template #value>{{ zeroVisitorZonesProductZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesProductZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('demoDashboard.charts.visitorsProduct')" icon="bi-bar-chart" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!visitorsByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByProductZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('demoDashboard.charts.avgDwellProduct')" icon="bi-stopwatch" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!avgDwellByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByProductZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('demoDashboard.charts.totalTimeProduct')" icon="bi-bar-chart-line" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!totalDurationRowsProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsProductZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('demoDashboard.charts.durationMixProduct')" icon="bi-grid" :height="360"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!percentageByDurationRangeInProductZone || !Object.keys(percentageByDurationRangeInProductZone).length"
                                        accent="#004F6E">
                                        <ChartsFixedWidgetsPDDPercentageByDuration
                                            :percentageByDurationRangeInZone="percentageByDurationRangeInProductZone"
                                            :zonePresent="isProductZonePresent()"
                                            :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('demoDashboard.charts.funnelProduct')" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + productZoneFunnelRows.length * 48)"
                                        :empty="!productZoneFunnelRows.length" accent="#004F6E">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('demoDashboard.funnel.zone') }}</div>
                                                <div class="num">{{ $t('demoDashboard.funnel.visitors') }}</div>
                                                <div class="num">{{ $t('demoDashboard.funnel.walkbys') }}</div>
                                                <div class="num">{{ $t('demoDashboard.funnel.shorts') }}</div>
                                                <div class="num">{{ $t('demoDashboard.funnel.clears') }}</div>
                                            </div>
                                            <div v-for="r in productZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('demoDashboard.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- Service response — "and did anyone come to them?"
                                 Belongs in Explore because it is a raw per-zone-type
                                 measurement, and in THIS section because Explore is
                                 already sectioned by zone type. Three panels: the
                                 number, then when it happened (consolidated across
                                 every measured zone of this type), then which zones.
                                 docs/reference/service_response_measurement.md §9 -->
                            <ServiceResponseTypePanel
                              :data="serviceResponseByType['6']"
                              :timeSpan="serviceResponsePeriodLabel"
                              accent="#004F6E"
                            />

                            <!-- ========================= TRIPWIRE SUB-PANEL =========================
                                 Demo product zones are all polygon today, so this sub-panel stays
                                 hidden. Kept structurally identical to production so a future
                                 tripwire product zone in demo_detection_zones.json renders Mode C
                                 estimates + confidence/bias chips with no template changes. -->
                            <SubPanel
                                v-if="hasTripwireProductZones"
                                :label="$t('demoDashboard.geometry.tripwire')"
                                :countText="$t('demoDashboard.count.zones', zoneStats.product.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#004F6E"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('demoDashboard.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#004F6E"
                                        :empty="!(bestProductZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestProductZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.crossings', { n: fmt(bestProductZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#004F6E"
                                        :empty="!(lowestProductZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestProductZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('demoDashboard.unit.crossings', { n: fmt(lowestProductZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#004F6E" size="sm"
                                        :empty="!crossingsByProductZone.length">
                                        <template #value>{{ crossingCoverageTextProductZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('demoDashboard.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#004F6E" size="sm"
                                        :empty="!zeroCrossingZonesProductZone.length">
                                        <template #value>{{ zeroCrossingZonesProductZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesProductZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>

                                    <KpiCard v-if="hasEstimatedProductZones"
                                        :label="$t('demoDashboard.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#004F6E"
                                        :empty="!(bestProductZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestProductZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]}`]"
>{{ confLabel(estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestProductZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('demoDashboard.charts.crossingsProduct')" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByProductZone?.some(r => r.crossings > 0)" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByProductZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedProductZones"
                                        :title="$t('demoDashboard.charts.estAvgDwellProduct')" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByProductZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.product[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.product[row.zone]}`]">{{ confLabel(estDwellConfidence.product[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('product', row.zone)" :class="biasChipFor('product', row.zone).cls">{{ biasChipFor('product', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedProductZones"
                                        :title="$t('demoDashboard.charts.estTotalProduct')" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByProductZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.product[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.product[row.zone]}`]">{{ confLabel(estDwellConfidence.product[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('product', row.zone)" :class="biasChipFor('product', row.zone).cls">{{ biasChipFor('product', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>
                        </Section>

                        <!-- Demo Zones — greyed teaser when not configured in demo.
                             Real <Section> for visual parity (collapsible chevron, badge,
                             sum-pills); .section-greyed wrapper dims the chrome. -->
                        <div v-if="!isDemoZonePresent()" v-show="exploreSubTab === 'sec-demo'" class="section-greyed">
                            <Section
                                id="sec-demo"
                                :title="$t('demoDashboard.sections.demo')"
                                icon="bi-easel"
                                                               accent="#62B5D9"
                                collapsible
                                :default-open="false"
                            >
                                <template #summary>
                                    <span class="sum-pill sum-zones">
                                        <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', 2) }}
                                    </span>
                                </template>
                            </Section>
                        </div>
                        <Section v-else v-show="exploreSubTab === 'sec-demo'" :title="$t('demoDashboard.sections.demoIntel')" :badge="selectedOpeningHoursMode" accent="#62B5D9">
                            <KpiGrid style="padding-top:20px">
                                <!-- Top Avg. Duration -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.topAvgDuration')"
                                    icon="bi-stopwatch"
                                    accent="#62B5D9"
                                    :loading="isLoadingbestDemoZoneInAvgDuration"
                                    :noZones="!isDemoZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.demo')"
                                    :empty="isCardEmpty(bestDemoZoneByAvgDuration?.zone_name)"
                                >
                                    <template #value>{{ bestDemoZoneByAvgDuration?.zone_name}}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.avg', { d: formattedDurationTime(bestDemoZoneByAvgDuration?.avg_duration || 0) }) }}</template>
                                </KpiCard>

                                <!-- Top Visitor Count -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.topVisitors')"
                                    icon="bi-people"
                                    accent="#62B5D9"
                                    :loading="isLoadingbestDemoZoneInCounts"
                                    :noZones="!isDemoZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.demo')"
                                    :empty="isCardEmpty(bestDemoZoneByVisitors?.zone_name)"
                                >
                                    <template #value>{{ bestDemoZoneByVisitors?.zone_name}}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(bestDemoZoneByVisitors?.visitor_count) }) }}</template>
                                </KpiCard>

                                <!-- Lowest Visitor Count -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.lowestVisitors')"
                                    icon="bi-person-dash"
                                    accent="#62B5D9"
                                    :loading="isLoadingworstDemoZoneInCounts"
                                    :noZones="!isDemoZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.demo')"
                                    :empty="!worstVisitorsFromRowsDemoZone"
                                >
                                    <template #value>{{ worstVisitorsFromRowsDemoZone?.zone }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(worstVisitorsFromRowsDemoZone?.visitors) }) }}</template>
                                </KpiCard>

                                <!-- Most Clear Lingerers (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.mostClearLingerers')"
                                    icon="bi-person-check"
                                    accent="#62B5D9"
                                    :noZones="!isDemoZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.demo')"
                                    :empty="!topClearLingerersDemoZone"
                                >
                                    <template #value>{{ topClearLingerersDemoZone?.zone }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.clearLingerers', { n: fmt(topClearLingerersDemoZone?.clears) }) }}</template>
                                </KpiCard>

                                <!-- Highest Engagement (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.highestEngagement')"
                                    icon="bi-emoji-heart-eyes"
                                    accent="#62B5D9"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="highestEngagementZoneDemoZone.visitors === 0"
                                >
                                    <template #value>{{ highestEngagementZoneDemoZone.zone }}</template>
                                    <template #meta>
                                        {{ fmt(highestEngagementZoneDemoZone.clears) }} / {{ fmt(highestEngagementZoneDemoZone.visitors) }}
                                        ({{ fmtPct(highestEngagementZoneDemoZone.ratio) }})
                                    </template>
                                </KpiCard>

                                <!-- Top Sales Potential (NEW) -->
                                <!-- <KpiCard
                                    :label="$t('demoDashboard.kpi.topEngagementPotential')"
                                    icon="bi-cash-coin"
                                    accent="#62B5D9"
                                    :noZones="!isDemoZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.demo')"
                                    :empty="!topSalesPotentialDemoZone"
                                >
                                    <template #value>{{ topSalesPotentialDemoZone?.zone }}</template>
                                    <template #meta>{{ asMoney(topSalesPotentialDemoZone?.engSales) }}</template>
                                </KpiCard> -->

                                <!-- Zone Coverage (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.zoneCoverage')"
                                    icon="bi-grid-3x3-gap"
                                    accent="#62B5D9"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="demoZoneNames.length === 0"
                                    size="sm"
                                >
                                    <template #value>{{ zoneCoverageTextDemoZone }}</template>
                                </KpiCard>

                                <!-- Zero-Visitor Zones (separate KPI) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.zeroVisitorZones')"
                                    icon="bi-x-circle"
                                    accent="#62B5D9"
                                    size="sm"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="!zeroVisitorZonesDemoZone.length"
                                >
                                    <!-- big number -->
                                    <template #value>{{ zeroVisitorZonesDemoZone.length }}</template>

                                    <!-- trimmed list, full in tooltip -->
                                    <template #meta>
                                        <span>
                                        {{ summarizeList(zeroVisitorZonesDemoZone, 4) }}
                                        </span>
                                    </template>
                                </KpiCard>
                            </KpiGrid>

                            <!-- DEMO charts (split into two panels, reusing demo chart comps) -->
                            <ChartGrid style="padding-top:20px">
                                <!-- Visitors by Zone (bar list) -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.visitorsDemo')"
                                    icon="bi-bar-chart"
                                    :height="340"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="!visitorsByDemoZone?.length"
                                    accent="#62B5D9"
                                >
                                    <div class="barlist">
                                        <div v-for="row in visitorsByDemoZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                        <span class="barlist-label">{{ row.zone }}</span>
                                        <div class="barlist-bar">
                                            <div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsDemoZone*100)+'%' }"></div>
                                        </div>
                                        <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                        </div>
                                    </div>
                                </ChartPanel>

                                <!-- Avg Dwell by Zone (bar list) -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.avgDwellDemo')"
                                    icon="bi-stopwatch"
                                    :height="340"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="!avgDwellByDemoZone?.length"
                                    accent="#62B5D9"
                                >
                                    <div class="barlist">
                                        <div v-for="row in avgDwellByDemoZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                        <span class="barlist-label">{{ row.zone }}</span>
                                        <div class="barlist-bar">
                                            <div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecDemoZone*100)+'%' }"></div>
                                        </div>
                                        <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                        </div>
                                    </div>
                                </ChartPanel>
                                
                                <!-- Total time spent per Demo zone -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.totalTimeDemo')"
                                    icon="bi-bar-chart-line"
                                    :height="320"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="!totalDurationPerDemoZone || !Object.keys(totalDurationPerDemoZone).length"
                                    accent="#62B5D9"
                                >
                                    <ChartsFixedWidgetsPDDTotalDuration
                                        :totalDurationPerZone="totalDurationPerDemoZone"
                                        :zonePresent="isDemoZonePresent()"
                                        :active="activeTab === 'data-foundation-layer'"
                                    />
                                </ChartPanel>

                                <!-- Duration mix (%) per Demo zone -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.durationMixDemo')"
                                    icon="bi-grid"
                                    :height="360"
                                    :noZones="!isDemoZonePresent()"
                                    :empty="!percentageByDurationRangeInDemoZone || !Object.keys(percentageByDurationRangeInDemoZone).length"
                                    accent="#62B5D9"
                                    >
                                    <ChartsFixedWidgetsPDDPercentageByDuration
                                        :percentageByDurationRangeInZone="percentageByDurationRangeInDemoZone"
                                        :zonePresent="isDemoZonePresent()"
                                        :active="activeTab === 'data-foundation-layer'"
                                    />
                                </ChartPanel>

                                <!-- Funnel Table -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.funnelDemo')"
                                    icon="bi-diagram-3"
                                    :height="Math.max(260, 56 + demoZoneFunnelRows.length * 48)"
                                    :empty="!demoZoneFunnelRows.length"
                                    accent="#62B5D9"
                                >
                                <div class="funnelz-table">
                                    <div class="funnelz-row funnelz-head">
                                        <div>{{ $t('demoDashboard.funnel.zone') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.visitors') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.walkbys') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.shorts') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.clears') }}</div>
                                    </div>

                                    <div
                                        v-for="r in demoZoneFunnelRows"
                                        :key="r.zone"
                                        class="funnelz-row"
                                    >
                                        <div class="zone-name">{{ r.zone }}</div>

                                        <div class="num">
                                            {{ fmt(r.visitors) }}
                                        </div>

                                        <!-- If insufficient data: merge the last 3 columns into one cell -->
                                        <template v-if="r.strategyInsufficient">
                                            <div class="merge-3 insufficient-msg">
                                                {{ $t('demoDashboard.funnel.insufficient') }}
                                            </div>
                                        </template>

                                        <!-- Otherwise: show the 3 normal columns -->
                                        <template v-else>
                                            <div class="num">
                                                <span>{{ fmt(r.walkbys.count) }}</span>
                                                <small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small>
                                            </div>

                                            <div class="num">
                                                <span>{{ fmt(r.shorts.count) }}</span>
                                                <small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small>
                                            </div>

                                            <div class="num">
                                                <span>{{ fmt(r.clears.count) }}</span>
                                                <small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                </ChartPanel>
                            </ChartGrid>
                        </Section>

                        <!-- Display Zones — greyed teaser when not configured in demo -->
                        <div v-if="!isDisplayZonePresent()" v-show="exploreSubTab === 'sec-display'" class="section-greyed">
                            <Section
                                id="sec-display"
                                :title="$t('demoDashboard.sections.display')"
                                icon="bi-image"
                                                               accent="#110D93"
                                collapsible
                                :default-open="false"
                            >
                                <template #summary>
                                    <span class="sum-pill sum-zones">
                                        <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', 7) }}
                                    </span>
                                </template>
                            </Section>
                        </div>
                        <Section v-else v-show="exploreSubTab === 'sec-display'" :title="$t('demoDashboard.sections.displayIntel')" :badge="selectedOpeningHoursMode" accent="#4F7EB3">
                            <!-- Zone identity 6.2: a zone became a Display Zone today. -->
                            <ChangeNotice
                                v-for="z in movedIntoDisplay" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[8])"
                            />
                            <KpiGrid style="padding-top:20px">
                                <!-- Top Avg. Duration -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.topAvgDuration')"
                                    icon="bi-stopwatch"
                                    accent="#4F7EB3"
                                    :loading="isLoadingbestDisplayZoneInAvgDuration"
                                    :noZones="!isDisplayZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.display')"
                                    :empty="isCardEmpty(bestDisplayZoneByAvgDuration?.zone_name)"
                                >
                                    <template #value>{{ bestDisplayZoneByAvgDuration?.zone_name}}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.avg', { d: formattedDurationTime(bestDisplayZoneByAvgDuration?.avg_duration || 0) }) }}</template>
                                </KpiCard>

                                <!-- Top Visitor Count -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.topVisitors')"
                                    icon="bi-people"
                                    accent="#4F7EB3"
                                    :loading="isLoadingbestDisplayZoneInCounts"
                                    :noZones="!isDisplayZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.display')"
                                    :empty="isCardEmpty(bestDisplayZoneByVisitors?.zone_name)"
                                >
                                    <template #value>{{ bestDisplayZoneByVisitors?.zone_name}}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(bestDisplayZoneByVisitors?.visitor_count) }) }}</template>
                                </KpiCard>

                                <!-- Lowest Visitor Count -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.lowestVisitors')"
                                    icon="bi-person-dash"
                                    accent="#4F7EB3"
                                    :loading="isLoadingworstDisplayZoneInCounts"
                                    :noZones="!isDisplayZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.display')"
                                    :empty="!worstVisitorsFromRowsDisplayZone"
                                >
                                    <template #value>{{ worstVisitorsFromRowsDisplayZone?.zone }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.visitors', { n: fmt(worstVisitorsFromRowsDisplayZone?.visitors) }) }}</template>
                                </KpiCard>

                                <!-- Most Clear Lingerers (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.mostClearLingerers')"
                                    icon="bi-person-check"
                                    accent="#4F7EB3"
                                    :noZones="!isDisplayZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.display')"
                                    :empty="!topClearLingerersDisplayZone"
                                >
                                    <template #value>{{ topClearLingerersDisplayZone?.zone }}</template>
                                    <template #meta>{{ $t('demoDashboard.unit.clearLingerers', { n: fmt(topClearLingerersDisplayZone?.clears) }) }}</template>
                                </KpiCard>

                                <!-- Highest Engagement (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.highestEngagement')"
                                    icon="bi-emoji-heart-eyes"
                                    accent="#4F7EB3"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="highestEngagementZoneDisplayZone.visitors === 0"
                                >
                                    <template #value>{{ highestEngagementZoneDisplayZone.zone }}</template>
                                    <template #meta>
                                        {{ fmt(highestEngagementZoneDisplayZone.clears) }} / {{ fmt(highestEngagementZoneDisplayZone.visitors) }}
                                        ({{ fmtPct(highestEngagementZoneDisplayZone.ratio) }})
                                    </template>
                                </KpiCard>

                                <!-- Top Sales Potential (NEW) -->
                                <!-- <KpiCard
                                    :label="$t('demoDashboard.kpi.topEngagementPotential')"
                                    icon="bi-cash-coin"
                                    accent="#4F7EB3"
                                    :noZones="!isDisplayZonePresent()"
                                    :noZonesText="$t('demoDashboard.noZones.display')"
                                    :empty="!topSalesPotentialDisplayZone"
                                >
                                    <template #value>{{ topSalesPotentialDisplayZone?.zone }}</template>
                                    <template #meta>{{ asMoney(topSalesPotentialDisplayZone?.engSales) }}</template>
                                </KpiCard> -->

                                <!-- Zone Coverage (NEW) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.zoneCoverage')"
                                    icon="bi-grid-3x3-gap"
                                    accent="#4F7EB3"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="displayZoneNames.length === 0"
                                    size="sm"
                                >
                                    <template #value>{{ zoneCoverageTextDisplayZone }}</template>
                                </KpiCard>

                                <!-- Zero-Visitor Zones (separate KPI) -->
                                <KpiCard
                                    :label="$t('demoDashboard.kpi.zeroVisitorZones')"
                                    icon="bi-x-circle"
                                    accent="#4F7EB3"
                                    size="sm"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="!zeroVisitorZonesDisplayZone.length"
                                >
                                    <!-- big number -->
                                    <template #value>{{ zeroVisitorZonesDisplayZone.length }}</template>

                                    <!-- trimmed list, full in tooltip -->
                                    <template #meta>
                                        <span>
                                        {{ summarizeList(zeroVisitorZonesDisplayZone, 4) }}
                                        </span>
                                    </template>
                                </KpiCard>
                            </KpiGrid>

                            <!-- Display charts (split into two panels, reusing display chart comps) -->
                            <ChartGrid style="padding-top:20px"> 
                                <!-- Visitors by Zone (bar list) -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.visitorsDisplay')"
                                    icon="bi-bar-chart"
                                    :height="340"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="!visitorsByDisplayZone?.length"
                                    accent="#4F7EB3"
                                >
                                    <div class="barlist">
                                        <div v-for="row in visitorsByDisplayZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                        <span class="barlist-label">{{ row.zone }}</span>
                                        <div class="barlist-bar">
                                            <div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsDisplayZone*100)+'%' }"></div>
                                        </div>
                                        <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                        </div>
                                    </div>
                                </ChartPanel>

                                <!-- Avg Dwell by Zone (bar list) -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.avgDwellDisplay')"
                                    icon="bi-stopwatch"
                                    :height="340"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="!avgDwellByDisplayZone?.length"
                                    accent="#4F7EB3"
                                >
                                    <div class="barlist">
                                        <div v-for="row in avgDwellByDisplayZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                        <span class="barlist-label">{{ row.zone }}</span>
                                        <div class="barlist-bar">
                                            <div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecDisplayZone*100)+'%' }"></div>
                                        </div>
                                        <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                        </div>
                                    </div>
                                </ChartPanel>
                                
                                <!-- Total time spent per Display zone -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.totalTimeDisplay')"
                                    icon="bi-bar-chart-line"
                                    :height="320"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="!totalDurationPerDisplayZone || !Object.keys(totalDurationPerDisplayZone).length"
                                    accent="#4F7EB3"
                                >
                                    <ChartsFixedWidgetsPDDTotalDuration
                                        :totalDurationPerZone="totalDurationPerDisplayZone"
                                        :zonePresent="isDisplayZonePresent()"
                                        :active="activeTab === 'data-foundation-layer'"
                                    />
                                </ChartPanel>

                                <!-- Duration mix (%) per Display zone -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.durationMixDisplay')"
                                    icon="bi-grid"
                                    :height="360"
                                    :noZones="!isDisplayZonePresent()"
                                    :empty="!percentageByDurationRangeInDisplayZone || !Object.keys(percentageByDurationRangeInDisplayZone).length"
                                    accent="#4F7EB3"
                                    >
                                    <ChartsFixedWidgetsPDDPercentageByDuration
                                        :percentageByDurationRangeInZone="percentageByDurationRangeInDisplayZone"
                                        :zonePresent="isDisplayZonePresent()"
                                        :active="activeTab === 'data-foundation-layer'"
                                    />
                                </ChartPanel>

                                <!-- Funnel Table -->
                                <ChartPanel
                                    :title="$t('demoDashboard.charts.funnelDisplay')"
                                    icon="bi-diagram-3"
                                    :height="Math.max(260, 56 + displayZoneFunnelRows.length * 48)"
                                    :empty="!displayZoneFunnelRows.length"
                                    accent="#4F7EB3"
                                >
                                <div class="funnelz-table">
                                    <div class="funnelz-row funnelz-head">
                                        <div>{{ $t('demoDashboard.funnel.zone') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.visitors') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.walkbys') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.shorts') }}</div>
                                        <div class="num">{{ $t('demoDashboard.funnel.clears') }}</div>
                                    </div>

                                    <div
                                        v-for="r in displayZoneFunnelRows"
                                        :key="r.zone"
                                        class="funnelz-row"
                                    >
                                        <div class="zone-name">{{ r.zone }}</div>

                                        <div class="num">
                                            {{ fmt(r.visitors) }}
                                        </div>

                                        <!-- If insufficient data: merge the last 3 columns into one cell -->
                                        <template v-if="r.strategyInsufficient">
                                            <div class="merge-3 insufficient-msg">
                                                {{ $t('demoDashboard.funnel.insufficient') }}
                                            </div>
                                        </template>

                                        <!-- Otherwise: show the 3 normal columns -->
                                        <template v-else>
                                            <div class="num">
                                                <span>{{ fmt(r.walkbys.count) }}</span>
                                                <small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small>
                                            </div>

                                            <div class="num">
                                                <span>{{ fmt(r.shorts.count) }}</span>
                                                <small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small>
                                            </div>

                                            <div class="num">
                                                <span>{{ fmt(r.clears.count) }}</span>
                                                <small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                </ChartPanel>
                            </ChartGrid>
                        </Section>

                        <!-- Info Zones — greyed teaser (no synthetic data in demo) -->
                        <div v-show="exploreSubTab === 'sec-info'" class="section-greyed">
                            <Section
                                id="sec-info"
                                :title="$t('demoDashboard.sections.info')"
                                icon="bi-info-circle"
                                                               accent="#0D9488"
                                collapsible
                                :default-open="false"
                            >
                                <template #summary>
                                    <span class="sum-pill sum-zones">
                                        <i class="bi bi-bounding-box"></i>{{ $t('demoDashboard.count.zones', 1) }}
                                    </span>
                                    <span class="sum-pill sum-tripwire">
                                        <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', 1) }}
                                    </span>
                                </template>
                            </Section>
                        </div>

                        <!-- Pick Up Zones — greyed teaser (no synthetic data in demo) -->
                        <div v-show="exploreSubTab === 'sec-pickup'" class="section-greyed">
                            <Section
                                id="sec-pickup"
                                :title="$t('demoDashboard.sections.pickup')"
                                icon="bi-bag-check"
                                                               accent="#7E22CE"
                                collapsible
                                :default-open="false"
                            >
                                <template #summary>
                                    <span class="sum-pill sum-tripwire">
                                        <i class="bi bi-arrow-left-right"></i>{{ $t('demoDashboard.count.tripwires', 1) }}
                                    </span>
                                </template>
                            </Section>
                        </div>

                    </div>
                </MDBTabPane>

                <!-- GUIDANCE -->
                <MDBTabPane tabId="actions-layer">
                    <storalytic-demo-guidance v-if="activeTab==='actions-layer'" :focus-zone="focusZone" />
                </MDBTabPane>
                
            </MDBTabContent>
        </MDBTabs>
    </div>

</template>

<script>
/* ============================================================
 * Imports
 * ========================================================== */
import { demoValueDelivered } from '@/demo/guidanceDemo.js'
import HeatmapDashboard from './HeatmapDashboard.vue'
import ChartsDashboardEntrance from './ChartsDashboardEntrance.vue'
import ChartsFixedWidgetsPDDTotalDuration from './ChartsFixedWidgetsPDDTotalDuration.vue'
import ChartsFixedWidgetsPDDPercentageByDuration from './ChartsFixedWidgetsPDDPercentageByDuration.vue'
import ProductZoneDeepDive from './ProductZoneDeepDive.vue'
import StoralyticDemoGuidance from './StoralyticDemoGuidance.vue'
import TodayHero from '@/components/ui/TodayHero.vue'
import StrategicPositioningChart from '@/components/StrategicPositioningChart.vue'
import PatternMatrix from '@/components/PatternMatrix.vue'
import { computePriorities } from '@/prioritization/priorities'
import { PATTERN_META } from '@/prioritization/settings'
import {
  composeStrategicPositioning,
  QUADRANT_META,
} from '@/prioritization/strategicPositioning'
import { hasAnyTripwire } from '@/utils/tripwireShape'

import KpiCard from '@/components/ui/KpiCard.vue'
import ChartPanel from '@/components/ui/ChartPanel.vue'
import ServiceResponseTypePanel from '@/components/ServiceResponseTypePanel.vue'
import CheckoutTransactionsPanel from '@/components/CheckoutTransactionsPanel.vue'
import CounterUnattendedPanel from '@/components/CounterUnattendedPanel.vue'
import CounterUnattendedCard from '@/components/CounterUnattendedCard.vue'

import DurationDropdown from './DurationDropdown.vue'
import DropdownOpeningHoursMode from './DropdownOpeningHoursMode.vue'

// Stora UI shells
import ControlHead from '@/components/ui/ControlHead.vue'
import Section from '@/components/ui/Section.vue'
import KpiGrid from '@/components/ui/KpiGrid.vue'
import ChartGrid from '@/components/ui/ChartGrid.vue'
import SubPanel from '@/components/ui/SubPanel.vue'
import ZoneTypeNav from '@/components/ui/ZoneTypeNav.vue'
import StoralyticDemoWeatherCard from './StoralyticDemoWeatherCard.vue'
import StoralyticDemoWeatherSignature from './StoralyticDemoWeatherSignature.vue'

import { MDBTabs, MDBTabNav, MDBTabContent, MDBTabItem, MDBTabPane } from 'mdb-vue-ui-kit'

import { onMounted, onBeforeUnmount, ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { changeFor, registerFor, withNewType } from '@/demo/changesTodayDemo'
import { NOTICES, ZONE_TYPE_NAMES, movedInto, entranceTypeChange, settingChanged } from '@/zones/changesToday'
import ChangeNotice from '@/components/ui/ChangeNotice.vue'
import { loadDemoJSON, demoDaysAgo } from '@/api/demoData.js'
import { useI18n } from 'vue-i18n'
import { formatNumber, formatEuro, formatPercent, formatDuration, formatDate, formatWeekday } from '@/i18n/format.js'

export default {
    name: 'FixedWidgets',

    emits: [
        'selectedDuration',
        'selectedOpeningHoursMode',
    ],

    components: {
        ChangeNotice,
        ServiceResponseTypePanel,
        CheckoutTransactionsPanel,
        CounterUnattendedPanel,
        CounterUnattendedCard,
        HeatmapDashboard,
        ChartsDashboardEntrance,
        ChartsFixedWidgetsPDDTotalDuration,
        ChartsFixedWidgetsPDDPercentageByDuration,
        ProductZoneDeepDive,
        StoralyticDemoGuidance,
        TodayHero,
        StrategicPositioningChart,
        PatternMatrix,
        DurationDropdown,
        DropdownOpeningHoursMode,
        MDBTabs,
        MDBTabNav,
        MDBTabContent,
        MDBTabItem,
        MDBTabPane,
        KpiCard,
        ChartPanel,
        ControlHead,
        Section,
        StoralyticDemoWeatherCard,
        StoralyticDemoWeatherSignature,
        KpiGrid,
        ChartGrid,
        SubPanel,
        ZoneTypeNav,
    },

    props: {
        fixedWidgetsData: {
            type: Object,
            required: true
        },
        fixedWidgetsCompareToData: {
            type: Object,
            required: true
        },
    },

    setup(props, { emit }) {
        // English stays exactly as it was (the sales demo); Dutch goes through
        // the shared formats in i18n/format.js.
        const { t, te, locale } = useI18n()
        const isNl = () => locale.value === 'nl'
        /* -----------------------------
        * App / UI state
        * --------------------------- */
        // IA reorg: land on Actions (the loop hub) → drill to Zones → Explore. No
        // tiers — everyone gets every tab.
        const activeTab = ref('actions-layer')
        const focusZone = ref('')   // deep-link: zone whose Actions card to scroll to + flash
        const route = useRoute()
        // ?scenario=type_change: zones that became a Display Zone today (6.2).
        const movedIntoDisplay = computed(() => movedInto(registerFor(route.query), 8))
        // 6.7: did a zone of these types get a new alert threshold today?
        const thresholdChangedIn = (typeIds) => (allZones.value || []).some((z) => z
            && typeIds.includes(z.DetectionZoneTypeId)
            && settingChanged(registerFor(route.query), 'thresholds', z.ZoneKey ?? z.DetectionZoneId)
            && !!((registerFor(route.query).settings.thresholds || {}).zones || []).length)
        // Zones tab: which zone-browser layout is showing (picker list vs card grid).
        const zoneBrowser = ref('picker')
        const showPrioritization = ref(false)

        // Demo stand-in for Allen's per-zone recommender (production fetches these
        // from /coach/recommendations). Keyed by zone name; Prio shows them as the
        // context-aware "what to do", with a "Track in Guidance" hand-off.
        const demoRecommendations = {
            'Power Tools': {
                action: 'Set up a plug-in demo unit customers can pick up and trigger, with a clear spec-and-price card and a "call an expert" button beside it.',
                why: 'Dwell here is healthy — the drop-off is the last step: turning genuine interest into a decision on an expensive tool.',
            },
            'Safety Shoes': {
                action: 'Bring a low display of the full size run to the aisle edge with a clear "sizes 39–48 in stock" sign, so the category registers from the main walkway.',
                why: 'Only about 7% of shoppers reach this aisle — versus roughly 15% at its best. It sits off the main route, so few people find it; the ones who do engage and buy well.',
            },
            'Garden Utilities': {
                action: 'Build a small focal display at the mouth of the aisle — one hero product staged for use — so passing shoppers have a reason to stop.',
                why: 'About 42% of your shoppers pass Garden but only ~22% stop. Traffic isn\'t the problem — the range just doesn\'t catch the eye from the aisle.',
            },
            'Promoted Articles Zone': {
                action: 'Add a bold price-and-benefit card and a limited-time cue to the promo, so the shoppers who stop get a clear reason to buy now.',
                why: 'Plenty of shoppers stop at the promo, but only about 14% buy — the offer isn\'t closing the sale.',
            },
        }
        const windowWidth = ref(window.innerWidth)
        const dateRangeMode = ref("duration")
        const selectedDuration  = ref(30)
        // Service response (docs/reference/service_response_measurement.md), keyed by
        // DetectionZoneTypeId so each Explore zone-type section reads its own block.
        const serviceResponseByType = ref({})
        const checkoutTransactions = ref(null)
        const counterUnattended = ref([])

        // Grouped per service — an info desk and a checkout are different problems with
        // different fixes, so they are never merged into one figure.
        const countersByService = computed(() => {
            const out = { checkout: [], info: [], service: [], pickup: [] }
            for (const c of counterUnattended.value) {
                if (out[c.service]) out[c.service].push(c)
            }
            return out
        })

        // The strip figure, checkout only and visit-weighted so a quiet checkout cannot
        // swing it. Same computation as the live Explore.
        const checkoutUnattendedRate = computed(() => {
            // Owner's-limit view (same as live Explore): 1 in N found a checkout unattended
            // past the limit the shop set (ownerThresholdWaits). Never "waited".
            const m = countersByService.value.checkout.filter(
                c => c.state === 'measured' && c.ownerThresholdWaits && c.ownerThresholdWaits.count > 0)
            const visits = m.reduce((n, c) => n + (c.visits || 0), 0)
            const affected = m.reduce((n, c) => n + (c.ownerThresholdWaits.count || 0), 0)
            if (!visits || !affected) return null
            return Math.max(2, Math.round(visits / affected))
        })
        const disabledDurations = ref([])
        const selectedOpeningHoursMode = ref('only_regular_opening_hours')
        const disabledOpeningHourModes = ref([])

        /* -----------------------------
        * Stores
        * --------------------------- */
        const uniqueDetectionZoneTypeIds = ref([])
        const allZones = ref(null)
        const productZoneCount = ref(0)
        const waitingZoneCount = ref(0)
        const interactionLaneCount = ref(0)
        const cashierLaneCount     = ref(0)
        const serviceZoneCount = ref(0)
        const infoZoneCount    = ref(0)
        const pickUpZoneCount  = ref(0)

        /* -----------------------------
        * Heatmap / entrance charts
        * --------------------------- */
        const heatmapData = ref([])
        const entranceChartData = ref({})

        /* -----------------------------
        * Store-level metrics
        * --------------------------- */ 
        const currentWeekday = ref()
        const todaysCurrentCount = ref(0)
        const todaysCurrentCombinedCountStoreIn = ref(0)
        const todaysCurrentCombinedCountStoreOut = ref(0)
        const todaysCurrentExit = ref(0)

        const averageVisitDuration = ref(0)
        const averageValueSameWeekday = ref(0)
        const averageValue = ref(0)

        const highestTotalHour = ref()
        const highestTotalHourCount = ref(0)
        const highestTotalDay = ref()
        const highestTotalDayCount = ref(0)
        const busiestHour = ref()
        const highestAverageHourlyCount = ref(0)
        const trend = ref("")
        // Structured companion to the trend sentence; absent on payloads
        // exported before the trend estimator was rebuilt.
        const trendDetail = ref(null)
        const storeTotalVisitors = ref(0)

        // Entrance data-outage caveat (demo). Only on the single-day view
        // ("Yesterday") so the 30-day demo stays clean — this mirrors the
        // pipeline's `store_data_integrity` flag; prod reads it off the widget.
        const storeDataIntegrity = computed(() =>
            selectedDuration.value === 1
                ? { status: 'partial_outage',
                    detail: t('demoDashboard.store.integrityDetail') }
                : null)

        /* -----------------------------
        * Product/Demo/Display data (PDD)
        * --------------------------- */
        const bestProductZoneByVisitors = ref({})
        const bestProductZoneByAvgDuration = ref({})
        const worstProductZoneByVisitors = ref({})
        const totalDurationPerProductZone = ref({})
        const percentageByDurationRangeInProductZone = ref({})
        const productZoneVisitorsCounts = ref({})
        const productZoneClearLingerersCounts = ref({})
        const productHotcoldByVisitors = ref([])
        const productHotcoldByClearLingerers = ref([])
        const productZoneFunnels = ref({})

        const bestDemoZoneByVisitors = ref({})
        const bestDemoZoneByAvgDuration = ref({})
        const worstDemoZoneByVisitors = ref({})
        const totalDurationPerDemoZone = ref({})
        const percentageByDurationRangeInDemoZone = ref({})
        const demoZoneVisitorsCounts = ref({})
        const demoZoneClearLingerersCounts = ref({})
        const demoHotcoldByVisitors = ref([])
        const demoHotcoldByClearLingerers = ref([])
        const demoZoneFunnels = ref({})

        const bestDisplayZoneByVisitors = ref({})
        const bestDisplayZoneByAvgDuration = ref({})
        const worstDisplayZoneByVisitors = ref({})
        const totalDurationPerDisplayZone = ref({})
        const percentageByDurationRangeInDisplayZone = ref({})
        const displayZoneVisitorsCounts = ref({})
        const displayZoneClearLingerersCounts = ref({})
        const displayHotcoldByVisitors = ref([])
        const displayHotcoldByClearLingerers = ref([])
        const displayZoneFunnels = ref({})

        /* -----------------------------
        * Checkout/Interaction KPIs
        * --------------------------- */
        const averageWaitingTime = ref(0)
        const averageTimeInZoneWaiting = ref(0)
        const maxWaitingDuration = ref(0)
        const maxWaitingZoneName = ref("")
        const mostTypicalWaitingTime = ref(0)
        const amountMostTypicalWaitingTime = ref(0)
        const mostTypicalWaitingTimeRangeLow = ref(0)
        const mostTypicalWaitingTimeRangeHigh = ref(0)
        const realWaitRate = ref(0)
        const realWaitCount = ref(0)

        // Checkout queue (line length from waiting-zone occupancy overlap).
        const checkoutQueue = ref(null)
        const queuePct = computed(() => Number(checkoutQueue.value?.pct_time_ge2 ?? 0))
        const queueVerdict = computed(() => {
            const p = queuePct.value
            if (p < 5) return t('demoDashboard.queue.rare')
            if (p < 15) return t('demoDashboard.queue.occasional')
            return t('demoDashboard.queue.often')
        })

        const averageInteractionTime = ref(0)
        const averageTimeInZoneInteraction = ref(0)
        const maxInteractionDuration = ref(0)
        const maxInteractionZoneName = ref("")
        const mostTypicalInteractionTime = ref(0)
        const amountMostTypicalInteractionTime = ref(0)
        const mostTypicalInteractionTimeRangeLow = ref(0)
        const mostTypicalInteractionTimeRangeHigh = ref(0)
        const realTransactionRate = ref(0)
        const realTransactionCount = ref(0)

        const fastestCheckoutZone = ref("")
        const slowestCheckoutZone = ref("")
        const fastestInteractionZone = ref("")
        const slowestInteractionZone = ref("")
        const mostUsedCheckout = ref("")
        const leastUsedCheckout = ref("")

        /* -----------------------------
        * Loading flags (per widget)
        * --------------------------- */
        const isLoadingAverageSameWeekday = ref(true)
        const isLoadingAverage = ref(true)
        const isLoadingHighestHour = ref(true)
        const isLoadingHighestDay = ref(true)
        const isLoadingBusiestHour = ref(true)
        const isLoadingTrend = ref(true)
        
        const isLoadingbestProductZoneInCounts = ref(true)
        const isLoadingbestProductZoneInAvgDuration = ref(true)
        const isLoadingworstProductZoneInCounts = ref(true)

        const isLoadingbestDemoZoneInCounts = ref(true)
        const isLoadingbestDemoZoneInAvgDuration = ref(true)
        const isLoadingworstDemoZoneInCounts = ref(true)

        const isLoadingbestDisplayZoneInCounts = ref(true)
        const isLoadingbestDisplayZoneInAvgDuration = ref(true)
        const isLoadingworstDisplayZoneInCounts = ref(true)

        const isLoadingaverageWaitingTime = ref(true)
        const isLoadingaverageTimeInZoneWaiting = ref(true)
        const isLoadingmaxWaitingDuration = ref(true)
        const isLoadingmaxWaitingZoneName = ref(true)
        const isLoadingmostTypicalWaitingTime = ref(true)
        const isLoadingRealWaitRate = ref(true)
        const isLoadingaverageInteractionTime = ref(true)
        const isLoadingaverageTimeInZoneInteraction = ref(true)
        const isLoadingmaxInteractionDuration = ref(true)
        const isLoadingmaxInteractionZoneName = ref(true)
        const isLoadingmostTypicalInteractionTime = ref(true)
        const isLoadingRealTransactionRate = ref(true)
        const isLoadingfastestCheckoutZone = ref(true)
        const isLoadingslowestCheckoutZone = ref(true)
        const isLoadingfastestInteractionZone = ref(true)
        const isLoadingslowestInteractionZone = ref(true)
        const isLoadingmostUsedCheckout = ref(true)
        const isLoadingleastUsedCheckout = ref(true)

        const isInsightsDisabled = true

        const loadJSON = (path, opts) => loadDemoJSON(path, opts)

        /* ==========================================================
        * Formatting helpers
        * ======================================================== */
        const fmt = (n) => (n == null ? '—' : (isNl() ? formatNumber(n) : n.toLocaleString()))
        
        // currency + safe division helpers
        const asMoney = (n) => 
            n == null 
                ? '—'
                : isNl() ? formatEuro(n) : n.toLocaleString(undefined, {
                    style:'currency',
                    currency:'EUR',
                    maximumFractionDigits:0
                })
        
        const safeDiv = (a,b) => (b && b !== 0) ? (a / b) : 0

        const fmtPct = (p) => (p==null ? '—' : isNl() ? formatPercent(p * 100, { decimals: 1 }) : `${(p*100).toFixed(1)}%`)

        const pct = (part, total) => total && part != null
            ? (isNl() ? formatPercent((part / total) * 100, { decimals: 1 }) : `${((part / total) * 100).toFixed(1)}%`)
            : '—'

        // "€11.67" / "€ 11,67"
        const eur2 = (n) => (isNl() ? formatEuro(n, { decimals: 2 }) : `€${Number(n).toFixed(2)}`)
        // A value that already is a percentage: "34.5%" / "34,5%".
        const pctVal = (v) => {
            if (!isNl()) return `${v}%`
            const num = Number(v)
            if (!Number.isFinite(num)) return `${v}%`
            const decimals = (String(v).split('.')[1] || '').length
            return formatPercent(num, { decimals })
        }
        // "high confidence" / "hoge betrouwbaarheid"; an unknown level keeps the old wording.
        const confLabel = (c) => {
            const key = `demoDashboard.confidence.${String(c || '').toLowerCase()}`
            return te(key) || te(key, 'en') ? t(key) : `${c} confidence`
        }

        // The duration selector uses sentinel IDs for the named ranges (this
        // week / this month / last week / last month) — the raw value is not
        // a day count. Translate it to the actual number of days in the
        // resolved period so labels like "over X days" stay truthful.
        // Human label for the service-response panels. This component has no
        // durationText of its own, so derive one from the resolved day count.
        const serviceResponsePeriodLabel = computed(() => {
            const d = effectiveDurationDays.value
            return d === 1 ? t('demoDashboard.period.yesterday') : t('demoDashboard.period.lastDays', { n: d })
        })

        const effectiveDurationDays = computed(() => {
            const d = selectedDuration.value
            // demo-today (NOT the real calendar day) — the this-week/this-month data is
            // anchored to it, so the "over X days" count + €/mo scaling must be too. UTC
            // getters to match the UTC-anchored rebasing.
            const now = demoDaysAgo(0)
            const daysInMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate()
            if (d === 1000) {
                const dow = (now.getUTCDay() + 6) % 7 // Mon=0 ... Sun=6
                return dow + 1
            }
            if (d === 2000) {
                return now.getUTCDate()
            }
            if (d === 3000) {
                return 7
            }
            if (d === 4000) {
                const lastMonth = now.getUTCMonth() === 0 ? 11 : now.getUTCMonth() - 1
                const lastMonthYear = now.getUTCMonth() === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear()
                return daysInMonth(lastMonthYear, lastMonth)
            }
            return Number(d) || 0
        })

        const formattedDurationTime = computed(() => isNl() ? (seconds) => formatDuration(seconds) : (seconds) => {
            // Round to whole seconds FIRST so the seconds part can never render as 60
            // (e.g. 899.6s -> "15 min 0 sec", not "14 min 60 sec").
            const total = Math.round(Number(seconds) || 0)
            if (total < 60) return `${total} sec`
            if (total < 3600) {
                const m = Math.floor(total / 60)
                const s = total % 60
                return `${m} min ${s} sec`
            }
            const h = Math.floor(total / 3600)
            const m = Math.floor((total % 3600) / 60)
            const s = total % 60
            return `${h} h ${m} min ${s} sec`
        })
        
        const fmtSec = (s) => 
            formattedDurationTime.value ? 
                formattedDurationTime.value(s) 
                : `${Math.round(s)}s`

        // Keep the list readable inside the card
        const summarizeList = (arr, n = 4) => {
            if (!arr?.length) return ''
            if (arr.length <= n) return arr.join(', ')
            const head = arr.slice(0, n).join(', ')
            return t('demoDashboard.more', { head, n: arr.length - n })
        }

        const hasInsufficientStrategyVisitorCutOff = (f) => {
            const s = String(f?.strategy ?? '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '_')
            return s === 'insufficient_data'
        }

        /* ==========================================================
        * Presence helpers (which zone-types exist)
        * ======================================================== */
        const isEntranceZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(1)
        }

        const isExitZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(2)
        }

        const isCombinedEntranceExitZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(3)
        }

        const isCheckoutWaitingZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(4)
        }

        const isCheckoutInteractionZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(5)
        }

        const isProductZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(6)
        }

        // Demo is Newell DIY — a DIY/home-improvement retailer. Production
        // pulls this from companyStore.company.CompanySettings.sector; demo
        // keeps it as a constant so the dashboard renders without a backend
        // round-trip. Propagated to every child that takes :segment-key
        // (TodayHero, StrategicPositioningChart, product-zone-deep-dive).
        const companySector = 'diy'

        // Demo doesn't carry a company-level staff-cost override — the
        // Capacity gauge falls back to the DIY sector default
        // (€28/hr from CAPACITY_VALUE_SETTINGS.diy).
        const companyStaffCost = null

        // PastPeakEngagement_p95 values used ONLY for the hero's
        // descriptive "In your top weeks, zones engaged X% of visitors…"
        // line. They are NOT consulted by resolveZoneCeiling anymore —
        // gauges + lever math always use the industry sector ceiling.
        // Values are deliberately a few pp above current engagement
        // so the line shows a positive € on the demo (rather than
        // collapsing to "you've never gone above these numbers"). On
        // real stores the pipeline writes these values from past data.
        // rung1Engagement = your own best-weeks engagement (the gauge's climb
        // 100%-anchor). Mirrors pastPeakEngagementP95; the real pipeline writes it.
        // Demo lever mix (see the ladder scratch): Promoted=engagement, Safety=reach,
        // Garden=conversion, Power Tools=good. Power/Garden sit at their engagement-best
        // (own-best 0.55 == current) so they read "good"/no-eng-gap; Garden's low
        // conversion makes conversion its lever. Own-best anchored to current avoids
        // pushing the ATTRACT gauge past 100%.
        // Must match DEMO_ZONE_OVERRIDES in StoralyticDemoHomePage — diverse levers:
        // Power Tools hold, Garden engagement, Safety reach (rung1Reach), Promoted conversion.
        const zoneOverrides = {
            'Power Tools':            { pastPeakEngagementP95: 0.50, rung1Engagement: 0.50 },
            'Garden Utilities':       { pastPeakEngagementP95: 0.46, rung1Engagement: 0.46 },
            'Safety Shoes':           { pastPeakEngagementP95: 0.44, rung1Engagement: 0.44, rung1Reach: 0.15 },
            'Promoted Articles Zone': { pastPeakEngagementP95: 0.42, rung1Engagement: 0.42 },
        }

        // Demo peer cohort (Rung 3) so the climb shows the "peers" rung. On real
        // stores the pipeline supplies this on fixedWidgetsData._cohort (cross-store p80).
        const prioFixedWidgetsData = computed(() => ({
            ...(props.fixedWidgetsData || {}),
            _cohort: { engagement: 0.42 },  // demo peer p80 (below Emax 0.50)
            // Demo SERVE four-rung (mirrors the backend _service.serve contract):
            // current wait 6:00, own-best 4:00, peers 3:20, best-in-class 3:00.
            _service: {
                serve: {
                    headlinePct: 66.7, peerTickPct: 120, ceilingTickPct: 133.3,
                    ownBestWaitSec: 240, cohortWaitSec: 200, currentWaitSec: 360, targetWaitSec: 180,
                    activeRung: 'your best weeks', activeRungKey: 'own',
                    waitReductionMin: 2.0,
                    // SERVE euro is a soft (est.) at-risk flag now (docs/11) — detail only, not a headline.
                    atRiskEur: 9400, atRiskRange: [6100, 12700], atRiskEst: true,
                    atFloor: false,
                    // backend shape: the store ladder rides under climb
                    climb: { activeRung: 'your best weeks', activeRungKey: 'own', waitReductionMin: 2.0,
                             currentWaitSec: 360, atFloor: false },
                },
                // Other waiting types (9/10/11): minutes only, no € (targetWait/ceiling null).
                waiting: [
                    { zoneType: 10, zoneTypeName: 'Service', headlinePct: 73, peerTickPct: 118, ceilingTickPct: null,
                      activeRung: 'your best weeks', waitReductionMin: 0.8, withinReachEur: null, atFloor: false },
                    { zoneType: 9, zoneTypeName: 'Info', headlinePct: 88, peerTickPct: 106, ceilingTickPct: null,
                      activeRung: 'your best weeks', waitReductionMin: 0.3, withinReachEur: null, atFloor: false },
                    { zoneType: 11, zoneTypeName: 'Pick Up', headlinePct: 100, peerTickPct: 112, ceilingTickPct: null,
                      activeRung: null, waitReductionMin: null, withinReachEur: null, atFloor: true },
                ],
            },
        }))

        // Demo compare-to: give SERVE a previous-period four-rung headline so its
        // delta (this period's headlinePct − previous') renders. 62 → +5pp vs 67.
        const prioCompareToData = computed(() => ({
            ...(props.fixedWidgetsCompareToData || {}),
            _service: { serve: { headlinePct: 61.7 } },
        }))

        // --- Today briefing (SEE): "Allen noticed" change-feed + the Store Performance
        // Digest. Mirrors the demo Home page so the briefing is one coherent read.
        const briefingFindings = ref([
            { id: 1, severity: 'warning', title: 'Longer checkout waits — Lane 2',
              detail: 'The average wait there is up about 45% — roughly 1m10s now, was about 48s.',
              when: 'this month vs last month' },
            { id: 2, severity: 'info', title: 'Garden Utilities — fewer visitors lately',
              detail: 'Visits are down about 14% versus the previous weeks. Allen predicts ~52 visitors/day next week.',
              when: 'recent-weeks trend + 7-day forecast' },
            { id: 3, severity: 'info', title: 'Promoted Articles — more visitors lately',
              detail: 'Visits are up about 9% versus the previous weeks. Allen predicts ~140 visitors/day next week.',
              when: 'recent-weeks trend + 7-day forecast' },
        ])
        const _SEV_RANK = { high: 0, warning: 1, info: 2 }
        const noticedFindings = computed(() =>
            [...briefingFindings.value]
                .filter(f => f.severity !== 'high')
                .sort((a, b) => _SEV_RANK[a.severity] - _SEV_RANK[b.severity]))
        const dismissBriefingFinding = (id) => {
            briefingFindings.value = briefingFindings.value.filter(f => f.id !== id)
        }
        const demoCoachDigest = ref(null)
        onMounted(async () => {
            // Service-response rollup. Failure must not take the store page down —
            // the panel renders nothing for a missing block.
            try {
                const sr = await loadDemoJSON('/demo_data/demo_service_response.json')
                serviceResponseByType.value = sr?.store?.byType ?? {}
                checkoutTransactions.value = await loadDemoJSON('/demo_data/demo_checkout_transactions.json')
                const cu = await loadDemoJSON('/demo_data/demo_counter_unattended.json')
                counterUnattended.value = (cu && cu.counters) || []
            } catch (e) {
                console.warn('Demo service-response rollup unavailable:', e)
            }

            try { demoCoachDigest.value = await loadJSON('/demo_data/demo_coach_digest.json') } catch (_) { /* noop */ }
        })

        // Active goals (mirrors the demo Home) — the goals the user set. Their
        // data-vs-goal reconciliation is narrated in the briefing digest.
        const activeGoals = [
            { title: 'Grow conversion in the Promoted Articles Zone', lever: 'conversion' },
            { title: 'Grow engagement in Garden Utilities', lever: 'engagement' },
            { title: 'Lift conversion on Safety Shoes', lever: 'conversion' },
        ]
        // PROVE — value delivered (run-rate + cumulative), CALCULATED from the same tracked
        // actions + € math as the Guidance/Actions tab (demo/guidanceDemo.js) so it can
        // never drift from it. (Currently returned but not rendered in this template.)
        const valueDelivered = (() => {
            const v = demoValueDelivered()
            if (!v) return null
            return {
                runRate: Math.round(v.runRate / 10) * 10,
                cumulative: Math.round(v.cumulative / 10) * 10,
                since: new Date(v.since + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            }
        })()

        const topPriorities = computed(() =>
            computePriorities(
                props.fixedWidgetsData,
                props.fixedWidgetsCompareToData,
                companySector,
                zoneOverrides,
                { engagement: 0.42 },   // demo peer cohort — MUST match Today's _cohort
            )
        )

        // Store-level product-funnel stats for Explore's at-a-glance (spec §5:
        // "€/visitor, %-engaged store stats → KEEP → Explore"). These are the two
        // cards that used to sit in the Intelligence strip — aggregates across all
        // product zones. Ratios, so they're period-independent (no €/mo scaling).
        const exploreProductVisitors = computed(() =>
            (topPriorities.value || []).reduce((a, z) => a + (z.visitors || 0), 0)
        )
        const exploreEngagedPct = computed(() => {
            const v = exploreProductVisitors.value
            if (!(v > 0)) return null
            const engaged = (topPriorities.value || []).reduce((a, z) => a + (z.visitors || 0) * (z.engagementRate || 0), 0)
            return engaged / v
        })
        const exploreEurPerVisitor = computed(() => {
            const v = storeTotalVisitors.value || exploreProductVisitors.value || 0
            if (!(v > 0)) return null
            const realized = (topPriorities.value || []).reduce(
                (a, z) => a + (z.visitors || 0) * (z.engagementRate || 0) * (z.conversionRate || 0) * (z.avgValue || 0), 0
            )
            return realized / v
        })

        // Per-zone strategic-role lookup from the Intelligence tab's
        // positioning composer, shaped for chip rendering ({icon,label,color}
        // per zone). Passed into TodayHero so the opportunity line shows
        // a strategic-role chip next to the zone name — and into any other
        // surface that wants the same cross-chart context.
        const strategicByZone = computed(() => {
            const sp = composeStrategicPositioning(props.fixedWidgetsData, companySector, zoneOverrides)
            const out = {}
            for (const p of sp.points || []) {
                const meta = QUADRANT_META[p.quadrant]
                if (!meta) continue
                // legend + interp surfaced for the strategic-role pill
                // tooltip on the Prio cards.
                out[p.zone] = {
                    icon: meta.icon,
                    label: meta.label,
                    color: meta.color,
                    legend: meta.legend,
                    interp: meta.interp,
                }
            }
            return out
        })

        // Zones-overview side panel: group zones by strategic role so the space
        // beside the quadrant is a readable, actionable key (Stars / Cold Spots …).
        const ROLE_MEANING = {
            'Stars': 'High traffic + high € per visit — your winners. Protect & replicate.',
            'High Runners': 'High traffic, lower € per visit — volume drivers. Lift basket / conversion.',
            'Spotlight Candidates': 'Low traffic, high € per visit — untapped. Draw more people in.',
            'Cold Spots': 'Low traffic + low € per visit — review placement or purpose.',
        }
        const QUADRANT_ORDER = ['Stars', 'High Runners', 'Spotlight Candidates', 'Cold Spots']
        // Display only: the English labels above stay the grouping keys.
        const ROLE_KEY = { 'Stars': 'star', 'High Runners': 'runner', 'Spotlight Candidates': 'spotlight', 'Cold Spots': 'cold' }
        const zoneRolesGrouped = computed(() => {
            // Per-zone stats from the ranked priorities, to enrich each role row.
            const pri = {}
            for (const p of topPriorities.value || []) pri[p.zone] = p
            const by = {}
            for (const [zone, m] of Object.entries(strategicByZone.value || {})) {
                const label = m.label || 'Other'
                if (!by[label]) by[label] = { label, color: m.color, icon: m.icon, meaning: ROLE_MEANING[label] || '', zones: [] }
                const p = pri[zone] || {}
                const eur = Math.round(p.potentialImpact || 0)
                const meta = PATTERN_META[p.patternKind] || PATTERN_META.neutral
                by[label].zones.push({
                    name: zone,
                    visitors: Math.round(p.visitors || 0),
                    engPct: Math.round((p.engagementRate || 0) * 100),
                    convPct: Math.round((p.conversionRate || 0) * 100),
                    eurText: eur > 0 ? (isNl() ? formatEuro(eur) : `€${eur.toLocaleString()}`) : '',
                    patternChip: t(`demoDashboard.patterns.${PATTERN_META[p.patternKind] ? p.patternKind : 'neutral'}`),
                    patternIcon: meta.icon,
                    patternColor: meta.color,
                    patternBg: meta.bg,
                })
            }
            return QUADRANT_ORDER.filter((l) => by[l]).map((l) => ({
                ...by[l],
                label: t(`demoDashboard.roles.${ROLE_KEY[l]}.label`),
                meaning: t(`demoDashboard.roles.${ROLE_KEY[l]}.meaning`),
            }))
        })
        // Clicking a role chip focuses that zone in the deep dive below.
        const zoneFocus = ref('')
        const heroFocusedZone = ref(null)
        // Default the Zones focus to the top zone so the funnel AND the active role
        // card agree on first load (the grid that used to seed selection is retired).
        watch(topPriorities, (list) => {
            if (!zoneFocus.value && !heroFocusedZone.value && list && list.length) {
                zoneFocus.value = list[0].zone
            }
        }, { immediate: true })
        function onHeroNavigate({ tab, zone, scroll } = {}) {
            if (zone) heroFocusedZone.value = zone
            if (tab && tab !== activeTab.value) {
                activeTab.value = tab
            }
            // "+N more" chip on the hero passes scroll:'bottom' — scroll the
            // page to the bottom of the deep-dive once it has rendered.
            if (scroll === 'bottom') {
                nextTick(() => {
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }))
                })
            }
        }

        // ---- Store Visitors section: zone summary pills + best-day lift ----
        const zoneStats = computed(() => {
            const zones = allZones.value || []
            const hasTrip = z => hasAnyTripwire(z?.DetectionZoneTripwirePoints)
            // Demo zones carry their polygon as a parsed array on `Polygon`;
            // accept either that or the production string field for symmetry.
            const hasPoly = z => {
                const raw = z?.Polygon ?? z?.DetectionZonePointArray
                let pts = raw
                if (typeof raw === 'string') {
                    try { pts = JSON.parse(raw) } catch { pts = null }
                }
                return Array.isArray(pts) && pts.length >= 3
            }
            const byType = (typeIds) => {
                const list = zones.filter(z => typeIds.includes(z.DetectionZoneTypeId))
                return {
                    polygon: list.filter(hasPoly).length,
                    tripwire: list.filter(hasTrip).length,
                }
            }
            return {
                entrance:    byType([1, 2, 3]),
                waiting:     byType([4]),
                interaction: byType([5]),
                product:     byType([6]),
                demo:        byType([7]),
                display:     byType([8]),
                info:        byType([9]),
                service:     byType([10]),
                pickup:      byType([11]),
            }
        })

        const bestDayLiftPct = computed(() => {
            const avg = Number(averageValue.value) || 0
            const peak = Number(highestTotalDayCount.value) || 0
            if (avg <= 0 || peak <= 0) return null
            return Math.round(((peak - avg) / avg) * 100)
        })

        // Collapse state per section. Phase 2 only exposes Store Visitors
        // explicitly; the rest mirror production defaults so future iterations
        // can plug in directly.
        // Explore is now sub-tabbed: the zone-type pills switch which single
        // section shows (default Store), replacing the old collapsible accordions.
        const exploreSubTab = ref('sec-store-visitors')

        // Zone-type sub-tab pills. Order follows the customer-journey arc
        // (Entrance → Waiting → Interaction → Service → Product → Demo → Display →
        // Info → Pick Up). In the demo every supported type is shown; types without
        // synthetic data render as `disabled` pills (greyed) so viewers see the full
        // capability set. Each pill carries its zone/tripwire counts from zoneStats.
        const zoneNavItems = computed(() => {
            const s = zoneStats.value
            return [
                { id: 'sec-store-visitors',       title: t('demoDashboard.sections.storeVisitors'), icon: 'bi-shop',                accent: '#36910d', tripwire: false, present: true, count: s.entrance },
                { id: 'sec-checkout-waiting',     title: t('demoDashboard.sections.checkoutWaiting'), icon: 'bi-hourglass-split',     accent: '#AD3935', tripwire: false, present: true, count: s.waiting,     disabled: !isCheckoutWaitingZonePresent(),     disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.checkoutWaitingZones') }) },
                { id: 'sec-checkout-interaction', title: t('demoDashboard.sections.checkoutInteraction'), icon: 'bi-credit-card-2-front', accent: '#D3A741', tripwire: false, present: true, count: s.interaction, disabled: !isCheckoutInteractionZonePresent(), disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.checkoutInteractionZones') }) },
                { id: 'sec-service',              title: t('demoDashboard.sections.service'), icon: 'bi-headset',             accent: '#D97706', tripwire: hasTripwireServiceZones.value,  present: true, count: s.service, disabled: !showServiceSection.value, disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.service') }) },
                { id: 'sec-product',              title: t('demoDashboard.sections.product'), icon: 'bi-tag',                 accent: '#004F6E', tripwire: hasTripwireProductZones.value,  present: true, count: s.product, disabled: !showProductSection.value, disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.product') }) },
                { id: 'sec-demo',                 title: t('demoDashboard.sections.demo'), icon: 'bi-easel',               accent: '#62B5D9', tripwire: false, present: true, count: s.demo,    disabled: !isDemoZonePresent(),    disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.demo') }) },
                { id: 'sec-display',              title: t('demoDashboard.sections.display'), icon: 'bi-image',               accent: '#110D93', tripwire: false, present: true, count: s.display, disabled: !isDisplayZonePresent(), disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.display') }) },
                { id: 'sec-info',                 title: t('demoDashboard.sections.info'), icon: 'bi-info-circle',         accent: '#0D9488', tripwire: false, present: true, count: s.info,    disabled: true, disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.info') }) },
                { id: 'sec-pickup',               title: t('demoDashboard.sections.pickup'), icon: 'bi-bag-check',           accent: '#7E22CE', tripwire: false, present: true, count: s.pickup,  disabled: true, disabledTitle: t('demoDashboard.notConfigured', { type: t('demoDashboard.sections.pickup') }) },
                { id: 'sec-weather',              title: t('demoDashboard.sections.weather'), icon: 'bi-cloud-sun',           accent: '#1f9d9d', tripwire: false, present: true, count: null },
            ]
        })
        const activeZoneItem = computed(() => zoneNavItems.value.find(i => i.id === exploreSubTab.value) || null)

        // ---- Mode C tripwire dwell estimation: store level + bias chips ----
        const storeDwellEstimateSeconds = ref(null)
        const storeDwellConfidence      = ref(null)
        const storeDwellBiasPct         = ref(null)

        // Per-zone-type bias dicts coming from the dashboard payload. The
        // backend emits these alongside *_zones_dwell_confidence; demo data
        // mirrors the shape so chips render against synthetic numbers.
        const estDwellBias = computed(() => {
            const d = props.fixedWidgetsData || {}
            const toObj = (v) => (v && typeof v === 'object') ? v : {}
            return {
                product: toObj(d.product_zones_dwell_bias_pct),
                demo:    toObj(d.demo_zones_dwell_bias_pct),
                display: toObj(d.display_zones_dwell_bias_pct),
                info:    toObj(d.info_zones_dwell_bias_pct),
                service: toObj(d.service_zones_dwell_bias_pct),
                pickup:  toObj(d.pick_up_zones_dwell_bias_pct),
            }
        })

        // Chip-config factory. Hidden below 5 % (within accuracy target),
        // "mild" 5-10 %, "high" ≥ 10 %. Mirrors the production thresholds.
        const biasChipForValue = (bias) => {
            const b = Number(bias ?? NaN)
            if (!Number.isFinite(b) || b < 5) return null
            const tier = b >= 10 ? 'high-bias' : 'mild-bias'
            return {
                label: t('demoDashboard.bias', { pct: b.toFixed(0) }),
                cls: ['conf-chip', `conf-${tier}`],
                title: b >= 10
                    ? `Tripwire detection bias ${b.toFixed(1)}%. The Brownian Bridge correction is compensating, but consider re-aiming the camera — the underlying signal is noisy.`
                    : `Tripwire detection bias ${b.toFixed(1)}%. Within acceptable range; minor calibration drift.`,
            }
        }
        const biasChipFor = (zoneType, zoneName) => {
            if (!zoneName) return null
            return biasChipForValue(estDwellBias.value?.[zoneType]?.[zoneName])
        }
        const confTitleFor = (zoneType, zoneName, conf) => {
            if (!conf) return ''
            const bias = Number(estDwellBias.value?.[zoneType]?.[zoneName] ?? NaN)
            const base = `Confidence: ${conf}.`
            if (!Number.isFinite(bias)) return base
            if (bias < 5) return `${base} Tripwire bias ${bias.toFixed(1)}% — well within accuracy target.`
            return `${base} Tripwire bias ${bias.toFixed(1)}% — see the bias chip for guidance.`
        }

        // Pull store-level dwell from the synthetic fixed-widgets payload.
        watch(() => props.fixedWidgetsData.store_dwell_estimate_seconds, (v) => {
            storeDwellEstimateSeconds.value = (v == null) ? null : Number(v)
        }, { immediate: true })
        watch(() => props.fixedWidgetsData.store_dwell_confidence, (v) => {
            storeDwellConfidence.value = v || null
        }, { immediate: true })
        watch(() => props.fixedWidgetsData.store_dwell_bias_pct, (v) => {
            storeDwellBiasPct.value = (v == null) ? null : Number(v)
        }, { immediate: true })

        const isDemoZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(7)
        }

        const isDisplayZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(8)
        }

        /* ==========================================================
        * Product derived data (lists, funnels, coverage)
        * ======================================================== */
        const productZoneFunnelsObj = computed(() => 
            props.fixedWidgetsData?.product_zone_funnels || {}
        )

        // zone names: prefer funnels keys, else visitors_counts keys
        const productZoneNames = computed(() => {
            const funnelKeys = Object.keys(productZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(productZoneVisitorsCounts.value || {})
        })

        const visitorsByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const f = productZoneFunnelsObj.value?.[z]
                const visitors = f != null
                ? Number(f.zone_visitors ?? 0)
                : Number(productZoneVisitorsCounts.value?.[z] ?? 0)
                return { zone: z, visitors }
            })
        )

        const avgDwellByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const total = totalDurationPerProductZone.value?.[z] ?? 0
                const vis = visitorsByProductZone.value.find(r => r.zone === z)?.visitors ?? 0
                return { zone: z, avgSec: safeDiv(total, vis) }
            })
        )

        const clearLingerersByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const f = productZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(productZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const f = productZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = productZoneFunnels.value?.[z] || {}
                const vis  = Number(productZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(productZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsProductZone = computed(() => 
            Math.max(1, ...visitorsByProductZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecProductZone = computed(() => 
            Math.max(1, ...avgDwellByProductZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersProductZone = computed(() => 
            [...clearLingerersByProductZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionProductZone = computed(() => 
            [...funnelsByProductZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialProductZone = computed(() => 
            [...funnelsByProductZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficProductZone = computed(() => 
            visitorsByProductZone.value.filter(x => x.visitors>0).length
        )
        
        const zoneCoverageTextProductZone = computed(() => 
            t('demoDashboard.coverage.withTraffic', { a: zonesWithTrafficProductZone.value, b: productZoneNames.value.length })
        )

        // Table rows for “Product Funnel by Zone”
        const zoneVisitorsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_visitors_counts || {}
        )
        
        const zoneWalkbysProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_walkbys_counts || {}
        )

        const zoneShortsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_clear_lingerers_counts || {}
        )
        
        const productZoneFunnelRows = computed(() => {
            const funnels = productZoneFunnelsObj.value || {}
            const funnelKeys = Object.keys(funnels)

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,          // ← flag for template
                            // keep these numeric fields (not rendered)
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })
                
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts
            const keys = new Set([
                ...Object.keys(zoneVisitorsProductZone.value || {}),
                ...Object.keys(zoneWalkbysProductZone.value || {}),
                ...Object.keys(zoneShortsProductZone.value || {}),
                ...Object.keys(zoneClearsProductZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                const visitors = Number(zoneVisitorsProductZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysProductZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsProductZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsProductZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,                  // ← always false in fallback
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsProductZone = computed(() => {
            const rows = (productZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsProductZone = computed(() => {
            const rowsAll = (productZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesProductZone = computed(() =>
            (productZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        /* ==========================================================
        * Product Zones: polygon / tripwire split + Mode C stubs
        *
        * Demo product zones are all polygon today. We still expose the
        * full set of bindings the production template references so the
        * Tripwire SubPanel and Mode C panels conditionally hide cleanly
        * (rather than crash) and a future tripwire product zone needs
        * only data, not template changes.
        * ========================================================== */
        const hasPolygonProductZones  = computed(() => zoneStats.value.product.polygon > 0)
        const hasTripwireProductZones = computed(() => zoneStats.value.product.tripwire > 0)
        const showProductSection      = computed(() =>
            hasPolygonProductZones.value || hasTripwireProductZones.value
        )

        // Polygon-filtered aliases. With no tripwire product zones the
        // unfiltered "best" matches the polygon "best".
        const bestProductZoneByAvgDurationPoly = computed(() => bestProductZoneByAvgDuration.value)
        const bestProductZoneByVisitorsPoly    = computed(() => bestProductZoneByVisitors.value)

        // Total-duration as a rows array (production switched from a chart
        // to a barlist in the polygon SubPanel).
        const totalDurationRowsProductZone = computed(() =>
            Object.entries(totalDurationPerProductZone.value || {})
                .map(([zone, totalSec]) => ({ zone, totalSec: Number(totalSec || 0) }))
                .filter(r => r.totalSec > 0)
        )
        const maxTotalDurationProductZone = computed(() =>
            Math.max(1, ...totalDurationRowsProductZone.value.map(x => x.totalSec))
        )

        // Per-zone-type dwell confidence map (Mode C). Backend keys are
        // *_zones_dwell_confidence; mirror the production shape so the
        // template can read `estDwellConfidence.product[zoneName]` etc.
        const estDwellConfidence = computed(() => {
            const d = props.fixedWidgetsData || {}
            const toObj = (v) => (v && typeof v === 'object') ? v : {}
            return {
                product: toObj(d.product_zones_dwell_confidence),
                demo:    toObj(d.demo_zones_dwell_confidence),
                display: toObj(d.display_zones_dwell_confidence),
                info:    toObj(d.info_zones_dwell_confidence),
                service: toObj(d.service_zones_dwell_confidence),
                pickup:  toObj(d.pick_up_zones_dwell_confidence),
            }
        })

        /* ----------------------------------------------------------
         * Tripwire Product Zones — synthetic Mode C data
         *
         * Two product zones in the demo carry tripwires:
         *   - Paint & Decor     — tripwire-only (in + out on one tripwire,
         *                         so it gets an estimated dwell)
         *   - Garden Utilities  — combined polygon + tripwire
         *
         * Paint & Decor is the demo's tripwire-only product zone: it has
         * tripwire data here and nothing a tripwire can't measure — no
         * engagement/conversion funnel, so it stays out of the € math,
         * the Zones tab and the gauges. Power Tools used to play this
         * role, but the rest of the demo gives it a full polygon funnel,
         * so it is a polygon zone now.
         *
         * The Tripwire SubPanel renders Top/Lowest Crossing Count +
         * Mode C dwell barlists for both, with per-zone confidence
         * and bias chips driven by product_zones_dwell_(confidence|
         * bias_pct) on fixedWidgetsData.
         * ---------------------------------------------------------- */
        const PRODUCT_ZONES_TRIPWIRE_DEMO = [
            { name: 'Paint & Decor',    crossings: 1716, estAvgDwellSec: 92.4,  estTotalSec: 158558 },
            { name: 'Garden Utilities', crossings: 1448, estAvgDwellSec: 64.0,  estTotalSec: 92672 },
        ]

        const hasEstimatedProductZones = computed(() => PRODUCT_ZONES_TRIPWIRE_DEMO.length > 0)

        const _productTripwireSortedByCrossings = computed(() =>
            [...PRODUCT_ZONES_TRIPWIRE_DEMO].sort((a, b) => b.crossings - a.crossings)
        )
        const _productTripwireSortedByEstDwell = computed(() =>
            [...PRODUCT_ZONES_TRIPWIRE_DEMO].sort((a, b) => b.estAvgDwellSec - a.estAvgDwellSec)
        )

        const bestProductZoneByCrossings = computed(() => {
            const top = _productTripwireSortedByCrossings.value[0]
            return top ? { zone_name: top.name, crossing_count: top.crossings } : null
        })
        const lowestProductZoneByCrossings = computed(() => {
            const arr = _productTripwireSortedByCrossings.value
            const bottom = arr[arr.length - 1]
            return bottom ? { zone_name: bottom.name, crossing_count: bottom.crossings } : null
        })
        const crossingsByProductZone = computed(() =>
            PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => ({ zone: z.name, crossings: z.crossings }))
        )
        const maxCrossingsProductZone = computed(() =>
            Math.max(1, ...PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => z.crossings))
        )
        // Zones that recorded crossings, out of the zones that carry a tripwire.
        const crossingCoverageTextProductZone = computed(() =>
            t('demoDashboard.coverage.zones', { a: PRODUCT_ZONES_TRIPWIRE_DEMO.length, b: zoneStats.value.product.tripwire })
        )
        const zeroCrossingZonesProductZone = computed(() => [])

        const bestProductZoneByEstAvgDwell = computed(() => {
            const top = _productTripwireSortedByEstDwell.value[0]
            return top ? { zone_name: top.name, avg_duration: top.estAvgDwellSec } : null
        })
        const estAvgDwellByProductZone = computed(() =>
            PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => ({ zone: z.name, avgSec: z.estAvgDwellSec }))
        )
        const estTotalTimeByProductZone = computed(() =>
            PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => ({ zone: z.name, totalSec: z.estTotalSec }))
        )
        const maxEstAvgSecProductZone    = computed(() => Math.max(1, ...PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => z.estAvgDwellSec)))
        const maxEstTotalTimeProductZone = computed(() => Math.max(1, ...PRODUCT_ZONES_TRIPWIRE_DEMO.map(z => z.estTotalSec)))

        /* ==========================================================
        * Service Zones — backed by synthetic tripwire-only zones. The
        * Customer Service Desk numbers mirror the aggregate of
        * demo_service_zone_analytics.json (so the section's KPIs stay
        * in sync with the deep-dive page). Returns Counter is demo-only
        * (no detail page — it's greyed in the NavSidebar).
        * ========================================================== */
        const SERVICE_ZONES_DEMO = [
            { name: 'Customer Service Desk', crossings: 1255, estAvgDwellSec: 85.4, estTotalSec: 107227 },
            { name: 'Returns Counter',       crossings: 642,  estAvgDwellSec: 142.5, estTotalSec: 91485 },
        ]

        const hasPolygonServiceZones  = computed(() => zoneStats.value.service.polygon > 0)
        const hasTripwireServiceZones = computed(() => zoneStats.value.service.tripwire > 0)
        const showServiceSection      = computed(() =>
            hasPolygonServiceZones.value || hasTripwireServiceZones.value
        )
        // Mode C estimated dwell is available when there's at least one
        // tripwire service zone (the synthetic file always carries the
        // estimate fields).
        const hasEstimatedServiceZones = computed(() => hasTripwireServiceZones.value)

        // Polygon-side stubs (demo has no polygon service zones).
        const bestServiceZoneByAvgDurationPoly = computed(() => null)
        const bestServiceZoneByVisitorsPoly    = computed(() => null)
        const zoneCoverageTextServiceZone      = computed(() =>
            t('demoDashboard.coverage.zones', hasPolygonServiceZones.value ? { a: zoneStats.value.service.polygon, b: zoneStats.value.service.polygon } : { a: 0, b: 0 })
        )

        // Tripwire-side data — picks the best/lowest by crossings count.
        const _serviceZonesSortedByCrossings = computed(() =>
            [...SERVICE_ZONES_DEMO].sort((a, b) => b.crossings - a.crossings)
        )
        const bestServiceZoneByCrossings = computed(() => {
            if (!hasTripwireServiceZones.value) return null
            const top = _serviceZonesSortedByCrossings.value[0]
            return { zone_name: top.name, crossing_count: top.crossings }
        })
        const lowestServiceZoneByCrossings = computed(() => {
            if (!hasTripwireServiceZones.value) return null
            const bottom = _serviceZonesSortedByCrossings.value[_serviceZonesSortedByCrossings.value.length - 1]
            return { zone_name: bottom.name, crossing_count: bottom.crossings }
        })
        const crossingsByServiceZone = computed(() =>
            hasTripwireServiceZones.value
                ? SERVICE_ZONES_DEMO.map(z => ({ zone: z.name, crossings: z.crossings }))
                : []
        )
        const maxCrossingsServiceZone = computed(() =>
            Math.max(1, ...SERVICE_ZONES_DEMO.map(z => z.crossings))
        )
        const crossingCoverageTextServiceZone = computed(() =>
            t('demoDashboard.coverage.zones', hasTripwireServiceZones.value ? { a: zoneStats.value.service.tripwire, b: zoneStats.value.service.tripwire } : { a: 0, b: 0 })
        )
        const zeroCrossingZonesServiceZone = computed(() => [])

        const _serviceZonesSortedByEstDwell = computed(() =>
            [...SERVICE_ZONES_DEMO].sort((a, b) => b.estAvgDwellSec - a.estAvgDwellSec)
        )
        const bestServiceZoneByEstAvgDwell = computed(() => {
            if (!hasEstimatedServiceZones.value) return null
            const top = _serviceZonesSortedByEstDwell.value[0]
            return { zone_name: top.name, avg_duration: top.estAvgDwellSec }
        })
        const estAvgDwellByServiceZone = computed(() =>
            hasEstimatedServiceZones.value
                ? SERVICE_ZONES_DEMO.map(z => ({ zone: z.name, avgSec: z.estAvgDwellSec }))
                : []
        )
        const estTotalTimeByServiceZone = computed(() =>
            hasEstimatedServiceZones.value
                ? SERVICE_ZONES_DEMO.map(z => ({ zone: z.name, totalSec: z.estTotalSec }))
                : []
        )
        const maxEstAvgSecServiceZone    = computed(() => Math.max(1, ...SERVICE_ZONES_DEMO.map(z => z.estAvgDwellSec)))
        const maxEstTotalTimeServiceZone = computed(() => Math.max(1, ...SERVICE_ZONES_DEMO.map(z => z.estTotalSec)))

        const highestEngagementZoneProductZone = computed(() => {
            const visitors = productZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = productZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        /* ==========================================================
        * Demo derived data (lists, funnels, coverage)
        * ======================================================== */
        const demoZoneFunnelsObj = computed(() => 
            props.fixedWidgetsData?.demo_zone_funnels || {}
        )

        const demoZoneNames = computed(() => {
            const funnelKeys = Object.keys(demoZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(demoZoneVisitorsCounts.value || {})
        })

        const visitorsByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const f = demoZoneFunnelsObj.value?.[z]
                const visitors = f != null
                ? Number(f.zone_visitors ?? 0)
                : Number(demoZoneVisitorsCounts.value?.[z] ?? 0)
                return { zone: z, visitors }
            })
        )

        const avgDwellByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const total = totalDurationPerDemoZone.value?.[z] ?? 0
                const vis = visitorsByDemoZone.value.find(r => r.zone === z)?.visitors ?? 0
                return { zone: z, avgSec: safeDiv(total, vis) }
            })
        )

        const clearLingerersByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const f = demoZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(demoZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const f = demoZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = demoZoneFunnels.value?.[z] || {}
                const vis  = Number(demoZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(demoZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsDemoZone = computed(() => 
            Math.max(1, ...visitorsByDemoZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecDemoZone = computed(() => 
            Math.max(1, ...avgDwellByDemoZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersDemoZone = computed(() => 
            [...clearLingerersByDemoZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionDemoZone = computed(() => 
            [...funnelsByDemoZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialDemoZone = computed(() => 
            [...funnelsByDemoZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficDemoZone = computed(() => 
            visitorsByDemoZone.value.filter(x => x.visitors>0).length
        )
        
        const zoneCoverageTextDemoZone = computed(() => 
            t('demoDashboard.coverage.withTraffic', { a: zonesWithTrafficDemoZone.value, b: demoZoneNames.value.length })
        )

        // Table rows for “Demo Funnel by Zone”
        const zoneVisitorsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_visitors_counts || {}
        )
        
        const zoneWalkbysDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_walkbys_counts || {}
        )

        const zoneShortsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_clear_lingerers_counts || {}
        )
        
        const demoZoneFunnelRows = computed(() => {
            const funnels = demoZoneFunnelsObj.value || {}
            const funnelKeys = Object.keys(funnels)

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })
                
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts
            const keys = new Set([
                ...Object.keys(zoneVisitorsDemoZone.value || {}),
                ...Object.keys(zoneWalkbysDemoZone.value || {}),
                ...Object.keys(zoneShortsDemoZone.value || {}),
                ...Object.keys(zoneClearsDemoZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                const visitors = Number(zoneVisitorsDemoZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysDemoZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsDemoZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsDemoZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsDemoZone = computed(() => {
            const rows = (demoZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsDemoZone = computed(() => {
            const rowsAll = (demoZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesDemoZone = computed(() =>
            (demoZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        const highestEngagementZoneDemoZone = computed(() => {
            const visitors = demoZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = demoZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        /* ==========================================================
        * Display derived data (lists, funnels, coverage)
        * ======================================================== */
        const displayZoneFunnelsObj = computed(() => 
            props.fixedWidgetsData?.display_zone_funnels || {}
        )

        const displayZoneNames = computed(() => {
            const funnelKeys = Object.keys(displayZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(displayZoneVisitorsCounts.value || {})
        })

        const visitorsByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const f = displayZoneFunnelsObj.value?.[z]
                const visitors = f != null
                ? Number(f.zone_visitors ?? 0)
                : Number(displayZoneVisitorsCounts.value?.[z] ?? 0)
                return { zone: z, visitors }
            })
        )

        const avgDwellByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const total = totalDurationPerDisplayZone.value?.[z] ?? 0
                const vis = visitorsByDisplayZone.value.find(r => r.zone === z)?.visitors ?? 0
                return { zone: z, avgSec: safeDiv(total, vis) }
            })
        )

        const clearLingerersByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const f = displayZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(displayZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const f = displayZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = displayZoneFunnels.value?.[z] || {}
                const vis  = Number(displayZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(displayZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsDisplayZone = computed(() => 
            Math.max(1, ...visitorsByDisplayZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecDisplayZone = computed(() => 
            Math.max(1, ...avgDwellByDisplayZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersDisplayZone = computed(() => 
            [...clearLingerersByDisplayZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionDisplayZone = computed(() => 
            [...funnelsByDisplayZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialDisplayZone = computed(() => 
            [...funnelsByDisplayZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficDisplayZone = computed(() => 
            visitorsByDisplayZone.value.filter(x => x.visitors>0).length
        )
        
        const zoneCoverageTextDisplayZone = computed(() => 
            t('demoDashboard.coverage.withTraffic', { a: zonesWithTrafficDisplayZone.value, b: displayZoneNames.value.length })
        )

        // Table rows for “Display Funnel by Zone”
        const zoneVisitorsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_visitors_counts || {}
        )
        
        const zoneWalkbysDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_walkbys_counts || {}
        )

        const zoneShortsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_clear_lingerers_counts || {}
        )
        
        const displayZoneFunnelRows = computed(() => {
            const funnels = displayZoneFunnelsObj.value || {}
            const funnelKeys = Object.keys(funnels)

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })
                
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts
            const keys = new Set([
                ...Object.keys(zoneVisitorsDisplayZone.value || {}),
                ...Object.keys(zoneWalkbysDisplayZone.value || {}),
                ...Object.keys(zoneShortsDisplayZone.value || {}),
                ...Object.keys(zoneClearsDisplayZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                const visitors = Number(zoneVisitorsDisplayZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysDisplayZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsDisplayZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsDisplayZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsDisplayZone = computed(() => {
            const rows = (displayZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsDisplayZone = computed(() => {
            const rowsAll = (displayZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesDisplayZone = computed(() =>
            (displayZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        const highestEngagementZoneDisplayZone = computed(() => {
            const visitors = displayZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = displayZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        /* ==========================================================
        * Trend & time formatting (computed)
        * ======================================================== */
        // "14h-15h" / "14:00–15:00"
        const hourWindow = (startHour, endHour) => isNl()
            ? `${String(startHour).padStart(2, '0')}:00–${String(endHour).padStart(2, '0')}:00`
            : `${startHour}h-${endHour}h`

        const formattedHighestTotalHour = computed(() => {
            if (!highestTotalHour.value) return { formattedDate: "", formattedTime: "" }

            const date = new Date(highestTotalHour.value)

            if (isNaN(date.getTime())) {
                console.error("Invalid Date Format:", highestTotalHour.value)
                return { formattedDate: "Invalid Date", formattedTime: "Invalid Time" }
            }

            // Extract date components
            const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
            const day = date.getDate()
            const month = date.toLocaleDateString('en-GB', { month: 'short' })

            const formattedDate = isNl()
                ? `${formatWeekday(date, { short: true })} ${formatDate(date, { style: 'short' })}`
                : `${weekday}, ${day} ${month}`
            
            // Extract time and compute the next hour
            const startHour = date.getHours()
            
            const endHour = (startHour + 1) % 24 // Ensure it stays within 24 hours

            const formattedTime = hourWindow(startHour, endHour)

            return {
                formattedDate,
                formattedTime
            }
        })

        const formattedHighestTotalDay = computed(() => {
    
            if (!highestTotalDay.value) return { formattedDate: ""}

            const date = new Date(highestTotalDay.value)

            if (isNaN(date.getTime())) {
                console.error("Invalid Date Format:", highestTotalDay.value)
                return { formattedDate: "Invalid Date"}
            }

            // Extract date components
            const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
            const day = date.getDate()
            const month = date.toLocaleDateString('en-GB', { month: 'short' })

            const formattedDate = isNl()
                ? `${formatWeekday(date, { short: true })} ${formatDate(date, { style: 'short' })}`
                : `${weekday}, ${day} ${month}`

            return {
                formattedDate
            }
        })

        const formattedBusiestHour = computed(() => {
            if (busiestHour.value === undefined || busiestHour.value === null) {
                return ""
            }

            const startHour = parseInt(busiestHour.value, 10)
            const endHour = (startHour + 1) % 24 // Calculate the end hour, wrapping around at 24

            return hourWindow(startHour, endHour)
        })

        const currentWeekdayString = computed(() => {
            if (currentWeekday.value >= 0 && currentWeekday.value <= 6) {
                return t(`demoDashboard.weekdays.${currentWeekday.value}`)
            } else {
                return t('demoDashboard.weekdays.same')
            }
        })

        const diffTodayAboveSameWeekdayAverage = computed(() => {
            let diff = averageValueSameWeekday.value - todaysCurrentCount.value - todaysCurrentCombinedCountStoreIn.value

            if (diff < 0) {
                diff = -1 * diff;
                return `+${diff}`
            } else if (diff > 0) {
                return `-${diff}`
            } else {
                return `${diff}`
            }
        })

        const diffTodayAboveAverage = computed(() => {
            let diff = averageValue.value - todaysCurrentCount.value - todaysCurrentCombinedCountStoreIn.value

            if (diff < 0) {
                diff = -1 * diff;
                return `+${diff}`
            } else if (diff > 0) {
                return `-${diff}`
            } else {
                return `${diff}`
            }
        })

        // map trend to icon/text/color
        const trendStyleMap = {
            strong_up:   { icon:'bi-arrow-up-square',   label:'Up High',   color:'#2a6041' },
            mod_up:      { icon:'bi-arrow-up-square',   label:'Up',        color:'#36910d' },
            slight_up:   { icon:'bi-arrow-up-square',   label:'Up Light',  color:'#2ceaa3' },
            stable:      { icon:'bi-arrow-right-square',label:'Stable',    color:'#3b3b3b' },
            slight_down: { icon:'bi-arrow-down-square', label:'Down Light',color:'#a7333f' },
            mod_down:    { icon:'bi-arrow-down-square', label:'Down',      color:'#C21500' },
            strong_down: { icon:'bi-arrow-down-square', label:'Down High', color:'#8b0000' },
        }

        // The demo's trend sentence is simulated English data. In Dutch the known
        // shapes get their Dutch sentence; anything else is shown as it came.
        const TREND_SENTENCES = [
            [/^Clear decline — about (\d+(?:\.\d+)?)% fewer visitors over the past (\d+) days\.$/, 'clearDecline'],
            [/^Clear increase — about (\d+(?:\.\d+)?)% more visitors over the past (\d+) days\.$/, 'clearIncrease'],
            [/^Leaning down — about (\d+(?:\.\d+)?)% lower over the past (\d+) days, but that is within normal day-to-day variation\. Not yet a trend\.$/, 'leaningDown'],
            [/^Leaning up — about (\d+(?:\.\d+)?)% higher over the past (\d+) days, but that is within normal day-to-day variation\. Not yet a trend\.$/, 'leaningUp'],
        ]
        function localTrendSentence(s) {
            if (!isNl() || typeof s !== 'string') return s
            for (const [re, key] of TREND_SENTENCES) {
                const m = s.trim().match(re)
                if (m) return t(`demoDashboard.trend.sentence.${key}`, { pct: formatNumber(Number(m[1]), { decimals: m[1].includes('.') ? 1 : 0 }), days: m[2] })
            }
            return s
        }

        // The store trend arrives as a sentence. `trend_detail` (structured) is
        // preferred when the payload carries it — string-matching prose is how
        // this tile silently went blank the moment the backend wording changed.
        function parseTrendString(trendStr, detail) {
            if (!trendStr || typeof trendStr !== 'string') return null

            const lower = trendStr.toLowerCase()
            const significant = detail ? detail.is_significant !== false : null

            // % across the selected period. Suppressed when there is no
            // established trend, so the tile shows a dash rather than a figure
            // that reads as a finding — matching the zone-level widget.
            const m = trendStr.match(/([+-]?\d+(\.\d+)?)\s*%/)
            let pct = detail ? (detail.total_change_pct ?? null)
                             : (m ? Number(m[1]) : null)

            // 1) bucket by prefix phrase. Current vocabulary first; the older
            //    %-per-day wording still appears in exports taken before the
            //    trend estimator was rebuilt.
            let bucket = null
            const noTrend = lower.startsWith('no clear trend')
                || lower.startsWith('leaning ')
                || lower.startsWith('not enough days')
                || lower.startsWith('no meaningful trend')
            if (noTrend || significant === false) {
                bucket = 'stable'
                pct = null
            } else if (lower.startsWith('clear increase')) {
                bucket = Math.abs(pct ?? 0) >= 25 ? 'strong_up'
                       : Math.abs(pct ?? 0) >= 10 ? 'mod_up' : 'slight_up'
            } else if (lower.startsWith('clear decline')) {
                bucket = Math.abs(pct ?? 0) >= 25 ? 'strong_down'
                       : Math.abs(pct ?? 0) >= 10 ? 'mod_down' : 'slight_down'
            }
            else if (lower.startsWith('strong increase')) bucket = 'strong_up'
            else if (lower.startsWith('moderate increase')) bucket = 'mod_up'
            else if (lower.startsWith('slight upward')) bucket = 'slight_up'
            else if (lower.startsWith('stable')) { bucket = 'stable'; pct = null }
            else if (lower.startsWith('slight decline')) bucket = 'slight_down'
            else if (lower.startsWith('moderate decline')) bucket = 'mod_down'
            else if (lower.startsWith('strong decrease')) bucket = 'strong_down'

            // 2) style + compose final text (keep server’s sentence)
            const style = bucket ? trendStyleMap[bucket] : null

            return {
                bucket,                   // e.g. "mod_up"
                pct,                      // null when no trend was established
                sentence: localTrendSentence(trendStr),       // original server string (Dutch when it matches)
                icon: style?.icon || 'bi-activity',
                color: style?.color || '#6b7a86',
                short: bucket === 'stable' ? '—' : (style ? t(`demoDashboard.trend.${bucket}`) : '—')
            }
        }

        const trendInfo = computed(() => parseTrendString(trend.value, trendDetail.value))

        /* ==========================================================
        * Methods: window + modal + background job
        * ======================================================== */

        const updateWindowWidth = () => {
            windowWidth.value = window.innerWidth;
        }

        const isCardEmpty = (...values) => {
            // Check if all values in the card are null, 0, empty string, or empty object
            return values.every(
                (val) =>
                    val === null ||
                    val === "null" ||
                    val === 0 ||
                    val === "0" ||
                    val === "" ||
                    (typeof val === "object" && Object.keys(val).length === 0)
            )
        }

        function armPrioritizationMount() {
            // small delay lets MDB switch from display:none to visible
            setTimeout(() => { showPrioritization.value = true }, 300)
        }

        watch(activeTab, (tab) => {
            if (tab === 'prioritization-layer') {
                armPrioritizationMount()
            } else {
                showPrioritization.value = false
            }
        })

        /* ==========================================================
        * Lifecycle
        * ======================================================== */
        onMounted(async () => {
            // isMounted.value = true

            // Deep-link from Today's gauge tap-in: ?tab=… opens that tab here.
            const _qtab = route.query && route.query.tab
            const _validTabs = new Set(['data-foundation-layer', 'intelligence-layer', 'actions-layer'])
            if (_qtab && _validTabs.has(_qtab)) {
                activeTab.value = _qtab
            }
            // Deep-link into a specific Explore sub-tab (gauge tap: SERVE → Checkout
            // Waiting, DEPLOY → Checkout Interaction).
            const _qsub = route.query && route.query.sub
            if (_qsub && zoneNavItems.value.some(i => i.id === _qsub)) {
                exploreSubTab.value = _qsub
            }
            // Deep-link from Today's "Act on this →": land on this zone's Actions card.
            const _qzone = route.query && route.query.zone
            if (_qzone) focusZone.value = String(_qzone)

            window.addEventListener('resize', updateWindowWidth)

            // ensure we arm Prioritization on first load if it’s the default tab
            await nextTick()
            if (activeTab.value === 'prioritization-layer') armPrioritizationMount()

            selectedOpeningHoursMode.value = 'only_regular_opening_hours'
            disabledOpeningHourModes.value = [
                'all',
                'regular_and_irregular_opening_hours'
            ]

            selectedDuration.value = 30
            // Demo only exposes the 30-day slot; every other period (incl. 90 days)
            // is greyed out — the frozen demo snapshot is a single 30-day window.
            disabledDurations.value = [
                '1', '7', '90', '365', '1000', '2000', '3000', '4000'
            ]
            

            const { detectionzones: _dz } = await loadJSON('/demo_data/demo_detection_zones.json')
            // ?scenario=type_change: the zone list already has the zone's NEW type (6.2).
            const _change = changeFor(route.query)
            const detectionzones = _change ? withNewType(_dz, _change.zone, _change.to) : _dz
            allZones.value = detectionzones
            uniqueDetectionZoneTypeIds.value = [...new Set(detectionzones.map(zone => zone.DetectionZoneTypeId))]
            productZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 6).length
            waitingZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 4).length
            interactionLaneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 5).length
            cashierLaneCount.value     = allZones.value.filter(z => z.DetectionZoneTypeId === 999).length
            infoZoneCount.value    = allZones.value.filter(z => z.DetectionZoneTypeId === 9).length
            serviceZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 10).length
            pickUpZoneCount.value  = allZones.value.filter(z => z.DetectionZoneTypeId === 11).length

            heatmapData.value = await loadJSON('/demo_data/demo_heatmap.json')
            let entrance_chart_result = await loadJSON('/demo_data/demo_entrance.json')
            entranceChartData.value = entrance_chart_result.entrance_chart_data
        })

        onBeforeUnmount(() => {
            window.removeEventListener('resize', updateWindowWidth);
        })

        /* ==========================================================
        * Watchers — map backend payload → UI state
        * ======================================================== */
        // store-level
        watch(() => props.fixedWidgetsData.current_weekday, (newValue) => {
            if (newValue !== undefined) {
                currentWeekday.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_same_weekday, (newValue) => {
            if (newValue !== undefined) {
                averageValueSameWeekday.value = Math.round(newValue)
                isLoadingAverageSameWeekday.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_visitors, (newValue) => {
            if (newValue !== undefined) {
                averageValue.value = Math.round(newValue)
                isLoadingAverage.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_hour, (newValue) => {
            if (newValue !== undefined) {
                highestTotalHour.value = newValue
                isLoadingHighestHour.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_hour_count, (newValue) => {
            if (newValue !== undefined) {
                highestTotalHourCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_day, (newValue) => {
            if (newValue !== undefined) {
                highestTotalDay.value = newValue
                isLoadingHighestDay.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_day_count, (newValue) => {
            if (newValue !== undefined) {
                highestTotalDayCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.busiest_hour, (newValue) => {
            if (newValue !== undefined) {
                busiestHour.value = `${newValue}`
                isLoadingBusiestHour.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_on_busiest_hour, (newValue) => {
            if (newValue !== undefined) {
                if (Math.round(newValue) < 1) {
                    highestAverageHourlyCount.value = '#';
                } else {
                    highestAverageHourlyCount.value = Math.round(newValue);
                }
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.trend_detail, (newValue) => {
            trendDetail.value = newValue ?? null
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.trend, (newValue) => {
            if (newValue !== undefined) {
                trend.value = newValue
                console.log(trend.value)
                isLoadingTrend.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.store_total_visitors, (newValue) => {
            if (newValue !== undefined) {
                storeTotalVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        // product
        watch(() => props.fixedWidgetsData.best_product_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestProductZoneByVisitors.value = newValue
                isLoadingbestProductZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_product_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestProductZoneByAvgDuration.value = newValue
                isLoadingbestProductZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_product_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstProductZoneByVisitors.value = newValue
                isLoadingworstProductZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_product_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerProductZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_product_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInProductZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                productZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                productZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                productHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                productHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                productZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // demo
        watch(() => props.fixedWidgetsData.best_demo_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestDemoZoneByVisitors.value = newValue
                isLoadingbestDemoZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_demo_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestDemoZoneByAvgDuration.value = newValue
                isLoadingbestDemoZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_demo_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstDemoZoneByVisitors.value = newValue
                isLoadingworstDemoZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_demo_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerDemoZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_demo_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInDemoZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                demoZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                demoZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                demoHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                demoHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                demoZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // display
        watch(() => props.fixedWidgetsData.best_display_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestDisplayZoneByVisitors.value = newValue
                isLoadingbestDisplayZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_display_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestDisplayZoneByAvgDuration.value = newValue
                isLoadingbestDisplayZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_display_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstDisplayZoneByVisitors.value = newValue
                isLoadingworstDisplayZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_display_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerDisplayZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_display_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInDisplayZone.value = newValue
            }
        }, { deep: true, immediate: true })
        
        watch(() => props.fixedWidgetsData.display_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                displayZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                displayZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                displayHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                displayHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                displayZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // waiting / interaction
        watch(() => props.fixedWidgetsData.average_waiting_time, (newValue) => {
            if (newValue !== undefined) {
                averageWaitingTime.value = newValue
                isLoadingaverageWaitingTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_time_in_zone_waiting, (newValue) => {
            if (newValue !== undefined) {
                averageTimeInZoneWaiting.value = newValue
                isLoadingaverageTimeInZoneWaiting.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_waiting_duration, (newValue) => {
            if (newValue !== undefined) {
                maxWaitingDuration.value = newValue
                isLoadingmaxWaitingDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_waiting_zone, (newValue) => {
            if (newValue !== undefined) {
                maxWaitingZoneName.value = newValue
                isLoadingmaxWaitingZoneName.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.most_typical_waiting_time, (newValue) => {
            if (newValue !== undefined) {
                if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                    // New shape: { value, count, low, high }
                    mostTypicalWaitingTime.value = newValue.value
                    amountMostTypicalWaitingTime.value = newValue.count
                    mostTypicalWaitingTimeRangeLow.value = newValue.low
                    mostTypicalWaitingTimeRangeHigh.value = newValue.high
                }
                else {
                    mostTypicalWaitingTime.value = null
                    amountMostTypicalWaitingTime.value = null
                    mostTypicalWaitingTimeRangeLow.value = 0
                    mostTypicalWaitingTimeRangeHigh.value = 0
                }
                isLoadingmostTypicalWaitingTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_wait_rate, (newValue) => {
            if (newValue !== undefined) {
                realWaitRate.value = Number(newValue) || 0
                isLoadingRealWaitRate.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_wait_count, (newValue) => {
            if (newValue !== undefined) {
                realWaitCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.checkout_queue, (newValue) => {
            if (newValue !== undefined) {
                checkoutQueue.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_interaction_time, (newValue) => {
            if (newValue !== undefined) {
                averageInteractionTime.value = newValue
                isLoadingaverageInteractionTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_time_in_zone_interaction, (newValue) => {
            if (newValue !== undefined) {
                averageTimeInZoneInteraction.value = newValue
                isLoadingaverageTimeInZoneInteraction.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_interaction_duration, (newValue) => {
            if (newValue !== undefined) {
                maxInteractionDuration.value = newValue
                isLoadingmaxInteractionDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                maxInteractionZoneName.value = newValue
                isLoadingmaxInteractionZoneName.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.most_typical_interaction_time, (newValue) => {
            if (newValue !== undefined) {
                if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                    mostTypicalInteractionTime.value = newValue.value
                    amountMostTypicalInteractionTime.value = newValue.count
                    mostTypicalInteractionTimeRangeLow.value = newValue.low
                    mostTypicalInteractionTimeRangeHigh.value = newValue.high
                }
                else {
                    mostTypicalInteractionTime.value = null
                    amountMostTypicalInteractionTime.value = null
                    mostTypicalInteractionTimeRangeLow.value = 0
                    mostTypicalInteractionTimeRangeHigh.value = 0
                }
                isLoadingmostTypicalInteractionTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_transaction_rate, (newValue) => {
            if (newValue !== undefined) {
                realTransactionRate.value = Number(newValue) || 0
                isLoadingRealTransactionRate.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_transaction_count, (newValue) => {
            if (newValue !== undefined) {
                realTransactionCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.fastest_checkout_zone, (newValue) => {
            if (newValue !== undefined) {
                fastestCheckoutZone.value = newValue
                isLoadingfastestCheckoutZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.slowest_checkout_zone, (newValue) => {
            if (newValue !== undefined) {
                slowestCheckoutZone.value = newValue
                isLoadingslowestCheckoutZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.fastest_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                fastestInteractionZone.value = newValue
                isLoadingfastestInteractionZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.slowest_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                slowestInteractionZone.value = newValue
                isLoadingslowestInteractionZone.value = false
            }
        }, { deep: true, immediate: true })


        watch(() => props.fixedWidgetsData.most_used_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                mostUsedCheckout.value = newValue
                isLoadingmostUsedCheckout.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.least_used_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                leastUsedCheckout.value = newValue
                isLoadingleastUsedCheckout.value = false
            }
        }, { deep: true, immediate: true })

        return {
            movedIntoDisplay, NOTICES, ZONE_TYPE_NAMES, entranceTypeChange, registerFor, settingChanged, thresholdChangedIn,
            serviceResponseByType,
            checkoutTransactions,
            counterUnattended,
            countersByService,
            checkoutUnattendedRate,
            serviceResponsePeriodLabel,
            // core
            activeTab,
            focusZone,
            zoneBrowser,
            zoneRolesGrouped,
            zoneFocus,
            demoRecommendations,
            selectedDuration,
            disabledDurations,
            dateRangeMode,
            selectedOpeningHoursMode,
            disabledOpeningHourModes,
            showPrioritization,

            // hero
            topPriorities,
            exploreEngagedPct,
            exploreEurPerVisitor,
            companySector,
            zoneOverrides,
            prioFixedWidgetsData,
            prioCompareToData,
            noticedFindings,
            dismissBriefingFinding,
            demoCoachDigest,
            activeGoals,
            valueDelivered,
            companyStaffCost,
            strategicByZone,
            heroFocusedZone,
            onHeroNavigate,

            // Store Visitors section
            zoneStats,
            bestDayLiftPct,
            exploreSubTab,
            zoneNavItems,
            activeZoneItem,

            // Mode C tripwire dwell + bias chips
            storeDwellEstimateSeconds,
            storeDwellConfidence,
            storeDwellBiasPct,
            estDwellBias,
            biasChipForValue,
            biasChipFor,
            confTitleFor,

            // charts & data
            heatmapData,
            entranceChartData,


            // formatting & helpers
            fmt,
            fmtPct,
            eur2,
            pctVal,
            confLabel,
            asMoney,
            fmtSec,
            formattedDurationTime,
            effectiveDurationDays,
            isCardEmpty,
            summarizeList,

            // store-level computed
            currentWeekdayString,
            todaysCurrentCount,
            todaysCurrentCombinedCountStoreIn,
            todaysCurrentCombinedCountStoreOut,
            todaysCurrentExit,
            averageVisitDuration,
            averageValueSameWeekday,
            diffTodayAboveSameWeekdayAverage,
            averageValue,
            diffTodayAboveAverage,
            highestTotalHour,
            highestTotalHourCount,
            highestTotalDay,
            highestTotalDayCount,
            busiestHour,
            highestAverageHourlyCount,
            trend,
            trendInfo,
            formattedHighestTotalHour,
            formattedHighestTotalDay,
            formattedBusiestHour,

            // PDD datasets
            bestProductZoneByVisitors,
            bestProductZoneByAvgDuration,
            worstProductZoneByVisitors,
            totalDurationPerProductZone,
            percentageByDurationRangeInProductZone,
            productZoneVisitorsCounts,
            productZoneClearLingerersCounts,
            productHotcoldByVisitors,
            productHotcoldByClearLingerers,
            productZoneFunnels,
            storeTotalVisitors,
            storeDataIntegrity,

            bestDemoZoneByVisitors,
            bestDemoZoneByAvgDuration,
            worstDemoZoneByVisitors,
            totalDurationPerDemoZone,
            percentageByDurationRangeInDemoZone,

            bestDisplayZoneByVisitors,
            bestDisplayZoneByAvgDuration,
            worstDisplayZoneByVisitors,
            totalDurationPerDisplayZone,
            percentageByDurationRangeInDisplayZone,

            // PDD funnels / lists / coverage
            productZoneNames,
            visitorsByProductZone,
            avgDwellByProductZone,
            funnelsByProductZone,
            topClearLingerersProductZone,
            topConversionProductZone,
            topSalesPotentialProductZone,
            zoneCoverageTextProductZone,
            maxVisitorsProductZone,
            maxAvgSecProductZone,
            productZoneFunnelRows,
            highestEngagementZoneProductZone,
            bestVisitorsFromRowsProductZone,
            worstVisitorsFromRowsProductZone,
            zeroVisitorZonesProductZone,

            // Product Zones polygon/tripwire split + Mode C
            hasPolygonProductZones,
            hasTripwireProductZones,
            showProductSection,
            hasEstimatedProductZones,
            bestProductZoneByAvgDurationPoly,
            bestProductZoneByVisitorsPoly,
            totalDurationRowsProductZone,
            maxTotalDurationProductZone,
            estDwellConfidence,
            bestProductZoneByEstAvgDwell,
            estAvgDwellByProductZone,
            estTotalTimeByProductZone,
            maxEstAvgSecProductZone,
            maxEstTotalTimeProductZone,
            bestProductZoneByCrossings,
            lowestProductZoneByCrossings,
            crossingsByProductZone,
            maxCrossingsProductZone,
            crossingCoverageTextProductZone,
            zeroCrossingZonesProductZone,

            // Service Zones polygon/tripwire split + Mode C
            hasPolygonServiceZones,
            hasTripwireServiceZones,
            showServiceSection,
            hasEstimatedServiceZones,
            bestServiceZoneByAvgDurationPoly,
            bestServiceZoneByVisitorsPoly,
            zoneCoverageTextServiceZone,
            bestServiceZoneByCrossings,
            lowestServiceZoneByCrossings,
            crossingsByServiceZone,
            maxCrossingsServiceZone,
            crossingCoverageTextServiceZone,
            zeroCrossingZonesServiceZone,
            bestServiceZoneByEstAvgDwell,
            estAvgDwellByServiceZone,
            estTotalTimeByServiceZone,
            maxEstAvgSecServiceZone,
            maxEstTotalTimeServiceZone,

            demoZoneNames,
            visitorsByDemoZone,
            avgDwellByDemoZone,
            funnelsByDemoZone,
            topClearLingerersDemoZone,
            topConversionDemoZone,
            topSalesPotentialDemoZone,
            zoneCoverageTextDemoZone,
            maxVisitorsDemoZone,
            maxAvgSecDemoZone,
            demoZoneFunnelRows,
            highestEngagementZoneDemoZone,
            bestVisitorsFromRowsDemoZone,
            worstVisitorsFromRowsDemoZone,
            zeroVisitorZonesDemoZone,

            displayZoneNames,
            visitorsByDisplayZone,
            avgDwellByDisplayZone,
            funnelsByDisplayZone,
            topClearLingerersDisplayZone,
            topConversionDisplayZone,
            topSalesPotentialDisplayZone,
            zoneCoverageTextDisplayZone,
            maxVisitorsDisplayZone,
            maxAvgSecDisplayZone,
            displayZoneFunnelRows,
            highestEngagementZoneDisplayZone,
            bestVisitorsFromRowsDisplayZone,
            worstVisitorsFromRowsDisplayZone,
            zeroVisitorZonesDisplayZone,
            
            // waiting / interaction
            averageWaitingTime,
            averageTimeInZoneWaiting,
            maxWaitingDuration,
            maxWaitingZoneName,
            mostTypicalWaitingTime,
            amountMostTypicalWaitingTime,
            mostTypicalWaitingTimeRangeLow,
            mostTypicalWaitingTimeRangeHigh,
            realWaitRate,
            realWaitCount,
            checkoutQueue,
            queuePct,
            queueVerdict,
            averageInteractionTime,
            averageTimeInZoneInteraction,
            maxInteractionDuration,
            maxInteractionZoneName,
            mostTypicalInteractionTime,
            amountMostTypicalInteractionTime,
            mostTypicalInteractionTimeRangeLow,
            mostTypicalInteractionTimeRangeHigh,
            realTransactionRate,
            realTransactionCount,
            fastestCheckoutZone,
            slowestCheckoutZone,
            fastestInteractionZone,
            slowestInteractionZone,
            mostUsedCheckout,
            leastUsedCheckout,

            // loading flags
            isLoadingAverageSameWeekday,
            isLoadingAverage,
            isLoadingHighestHour,
            isLoadingHighestDay,
            isLoadingBusiestHour,
            isLoadingTrend,
            isLoadingbestProductZoneInCounts,
            isLoadingbestProductZoneInAvgDuration,
            isLoadingworstProductZoneInCounts,
            isLoadingbestDemoZoneInCounts,
            isLoadingbestDemoZoneInAvgDuration,
            isLoadingworstDemoZoneInCounts,
            isLoadingbestDisplayZoneInCounts,
            isLoadingbestDisplayZoneInAvgDuration,
            isLoadingworstDisplayZoneInCounts,
            isLoadingaverageWaitingTime,
            isLoadingaverageTimeInZoneWaiting,
            isLoadingmaxWaitingDuration,
            isLoadingmaxWaitingZoneName,
            isLoadingmostTypicalWaitingTime,
            isLoadingRealWaitRate,
            isLoadingaverageInteractionTime,
            isLoadingaverageTimeInZoneInteraction,
            isLoadingmaxInteractionDuration,
            isLoadingmaxInteractionZoneName,
            isLoadingRealTransactionRate,
            isLoadingmostTypicalInteractionTime,
            isLoadingfastestCheckoutZone,
            isLoadingslowestCheckoutZone,
            isLoadingfastestInteractionZone,
            isLoadingslowestInteractionZone,
            isLoadingmostUsedCheckout,
            isLoadingleastUsedCheckout,
            
            // presence flags
            isEntranceZonePresent,
            isExitZonePresent,
            isCombinedEntranceExitZonePresent,
            isCheckoutWaitingZonePresent,
            isCheckoutInteractionZonePresent,
            isProductZonePresent,
            isDemoZonePresent,
            isDisplayZonePresent,

            isInsightsDisabled,

            // lane counts
            productZoneCount,
            waitingZoneCount,
            interactionLaneCount,
            cashierLaneCount,
            serviceZoneCount,
            infoZoneCount,
            pickUpZoneCount,
        }
    }
}

</script>

<style scoped>
/* ===== Explore "at a glance" strip (Data → Explore, IA reorg) =====
   No title/subtitle — the tab names it and the UI is self-evident. The strip
   hugs its tiles and sits as a light headline above the jump-nav + sections. */
.explore-subtitle {
  display: flex; align-items: center; gap: 8px;
  margin: 12px 0 14px; font-size: 1.05rem; font-weight: 700; color: #2b3a46;
}
.explore-subtitle i { font-size: 1.1rem; }
.explore-glance {
  display: inline-flex;
  align-items: stretch;
  gap: 4px;
  margin: 2px 0 22px;
  padding: 12px 12px;
  background: #f7f9fc;
  border: 1px solid #e3ebf5;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(29, 43, 58, 0.05);
  max-width: 100%;
  flex-wrap: wrap;
}
.eg-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 20px;
  min-width: 92px;
}
.eg-tile + .eg-tile { border-left: 1px solid #e3ebf5; }
.eg-val {
  font-size: 20px;
  font-weight: 650;
  color: #3a4a57;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.eg-lbl {
  font-size: 11px;
  font-weight: 600;
  color: #7a8895;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
/* Explore zone-type headers: the global .section-head h5 is 1.6rem — too heavy
   for this dense reference list. Scope a lighter size + weight here so the
   accent-coloured names read as calm headers, not a stack of shouting titles.
   Other Section uses (Actions lifecycle, etc.) keep the global size. */
.data-foundation-pane :deep(.section-head h5) {
  font-size: 1.18rem;
  font-weight: 600;
}
@media (max-width: 720px) {
  .explore-glance { width: 100%; justify-content: space-between; }
}

/* ============================================================
 TODAY surface (IA reorg) — one cohesive design system
   Rhythm: a calm vertical stack of sections, each with a consistent
   header; the two summary panels pair up at the bottom so nothing
   reads as a lonely afterthought.
============================================================ */
.today {
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 1060px;
  margin-right: 20px;
  padding-top: 4px;
}

/* Consistent section header (icon + label + optional right-hand link). */
.today-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.today-head-title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #2b3a46;
  display: flex;
  align-items: center;
  gap: 8px;
}
.today-head-title .bi { color: var(--stora-accent, #4F7EB3); font-size: 15px; }
.today-link {
  border: none; background: none; cursor: pointer;
  font-size: 12px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
  display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  padding: 2px 2px 2px 6px; border-radius: 6px; transition: background .15s ease;
}
.today-link:hover { background: #eef4fb; }

/* ---- The lead: one Allen briefing card ---- */
.allen-briefing {
  background: #fff;
  border: 1px solid #e8eef7;
  border-radius: 16px;
  padding: 20px 24px 22px;
  box-shadow: 0 8px 22px -14px rgba(13,38,76,0.16);
}
.allen-briefing-head {
  display: flex; align-items: center; gap: 12px;
  padding-bottom: 14px; margin-bottom: 16px;
  border-bottom: 1px solid #eef2f7;
}
.allen-briefing-avatar {
  width: 36px; height: 36px; border-radius: 50%; flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #4F7EB3, #62B5D9); color: #fff; font-size: 1.05rem;
  box-shadow: 0 4px 10px rgba(79,126,179,0.30);
}
.allen-briefing-id { display: flex; flex-direction: column; gap: 1px; }
.allen-briefing-title { font-weight: 700; color: #1d2b3a; font-size: 1.02rem; line-height: 1.2; }
.allen-briefing-meta { font-size: 0.78rem; color: #8a99a4; }
.allen-briefing-read { display: block; margin-top: 16px; color: #425461; font-size: 0.92rem; line-height: 1.6; }
.allen-briefing-flagged { display: block; margin-top: 18px; padding-top: 16px; border-top: 1px solid #eef2f7; }

/* ---- Bottom pair: value delivered + goals ---- */
.today-duo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.today-panel {
  background: #fff;
  border: 1px solid #e8eef7;
  border-radius: 14px;
  padding: 16px 18px 18px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.03);
}
.today-panel .today-head { margin-bottom: 12px; }
/* Value panel — a quiet green edge marks it as banked value (PROVE). */
.today-panel--value { border-left: 3px solid #62a583; }
.value-hero { display: flex; align-items: baseline; gap: 8px; }
.value-eur { font-size: 30px; font-weight: 800; color: #2c6f58; line-height: 1; letter-spacing: -0.5px; }
.value-per { font-size: 12px; font-weight: 600; color: #6b7a87; }
.value-sub {
  margin-top: 10px; font-size: 12px; color: #6b7a87;
  display: flex; align-items: center; gap: 7px;
}
.value-sub .bi { color: #7fb096; font-size: 13px; }

.today-goals { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 11px; }
.today-goal { display: flex; align-items: flex-start; gap: 10px; }
.goal-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex: 0 0 auto; background: #8fa2b7; }
.goal-dot.lever-conversion { background: #4F7EB3; }
.goal-dot.lever-engagement { background: #97a83f; }
.goal-dot.lever-reach { background: #C08A2E; }
.today-goal-title { font-size: 13px; color: #2b3a46; line-height: 1.4; }

@media (max-width: 860px) {
  .today-duo { grid-template-columns: 1fr; }
}

/* ============================================================
 TREND CARD CONTENT
============================================================ */
.trend-short {
  font-weight: 700;
  font-size: 1.0rem;
  line-height: 1.1;
}
.trend-sentence {
  display: block;
  color: #6b7a86;
  font-size: .9rem;
  line-height: 1.25;
}

/* ============================================================
 BAR-LIST (Visitors/Average dwell lists)
============================================================ */
/* Single-grid layout (mirrors production) so the label column auto-sizes
   to the widest label — single-zone charts get a tight label; charts
   with longer names or conf-chips take more space without forcing
   whitespace in other charts. */
.barlist { display:grid; grid-template-columns: max-content 1fr auto; gap:10px; align-items:center; }
.barlist-row { display:contents; }
.barlist-label { color:#425461; white-space:nowrap; }
.barlist-row.with-chip .barlist-label { display:inline-flex; align-items:center; gap:6px; }
.barlist-row.with-chip .barlist-label .conf-chip { flex-shrink: 0; }
.barlist-bar { height:10px; background:#eef3f7; border-radius:999px; overflow:hidden; }
.barlist-fill { height:100%; background:#004F6E; border-radius:999px; opacity:.85; }
.barlist-value { font-variant-numeric: tabular-nums; color:#2b3a44; }

/* Confidence / bias chips (Mode C tripwire dwell). Mirrors production
   FixedWidgets.vue — backend emits only 'high' or 'medium'; anything
   weaker is suppressed entirely. Bias chips share the same shape so a
   row carrying both stays scannable, but use outlined treatments to
   stay visually distinct. */
.conf-chip{
    display: inline-block;
    margin-left: 4px;
    padding: 1px 6px;
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 999px;
    border: 1px solid transparent;
}
.integrity-badge{
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #b45309;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 999px;
    padding: 1px 7px;
    cursor: help;
}
.conf-chip.conf-high      { color: #15803d; background: #dcfce7; border-color: #86efac; }
.conf-chip.conf-medium    { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
.conf-chip.conf-mild-bias { color: #92400e; background: #fffbeb; border-color: #fde68a; }
.conf-chip.conf-high-bias { color: #b91c1c; background: #fee2e2; border-color: #fca5a5; }

/* ============================================================
 FUNNEL TABLE
============================================================ */
.funnelz-table { display: grid; gap: 8px; }
.funnelz-row {
  display: grid;
  grid-template-columns: 1fr .6fr .8fr .9fr .9fr;
  align-items: center;
  gap: 10px;
  padding: .45rem .65rem;
  border: 1px solid #e6edf3;
  border-radius: 10px;
  background: #fff;
}
.funnelz-head { font-weight: 600; background: #f7fafc; }
.zone-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.num small { color: #6b7a86; margin-left: 4px; }
.funnelz-row .merge-3 { grid-column: 3 / span 3; text-align: center; }
.funnelz-row .insufficient-msg { color: #6b7a86; font-style: italic; }

/* ============================================================
 STATE / UTILITY
============================================================ */
.card-disabled { opacity: 0.5; pointer-events: none; background-color: #ffffff; }
.group-disabled { border: 2px dashed #dcdcdc; background-color: #ffffff; opacity: 0.7; }

/* ============================================================
 MODAL (Allen’s insights)
============================================================ */
.toggle-popup:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none; /* redundant for buttons, but keeps hover effects off */
}

.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 2100;   /* above the nav sidebar (1100) */
}
.modal-window {
  background: white; border-radius: 8px;
  /* no sidebar offset — centered over the full viewport; fluid min-width */
  min-width: min(400px, 92vw); max-width: 92vw; max-height: 90vh;
  overflow: auto; padding: 1rem; position: relative;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

/* ============================================================
 LOADER (overlay)
============================================================ */
.loader-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background-color: rgba(255, 255, 255, 0.8); z-index: 1000;
}
.loader {
  width: 20px; margin-top: 20px; padding: 2px; aspect-ratio: 1; border-radius: 50%;
  background: #B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m); mask: var(--_m);
  -webkit-mask-composite: source-out; mask-composite: subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn) } }

/* ============================================================
 RESPONSIVE (kept only where still referenced)
============================================================ */
@media (max-width: 1600px) {
  .charts-grid { grid-template-columns: 1fr; }
  .grid-container-product-data { grid-template-columns: 1fr; }
}
@media (max-width: 1500px) {
  .grid-container-product-data { grid-template-columns: 1fr; }
  .card-stack { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-right: 0; }
}
@media (max-width: 1400px) {
  .chart-container { flex-direction: column; gap: 1rem; }
  .chart-wrapper { flex: 1 1 100%; max-width: 100%; }
}
@media (max-width: 900px){
  .funnelz-row { grid-template-columns: 1.2fr .6fr .8fr .9fr .9fr; }
}
/* (.modal-window no longer offset — centered over the viewport at all widths) */
@media (max-width: 600px) {
  .grid-container-product-data { grid-template-columns: 1fr; }
  .card-stack { display: grid; grid-template-columns: 1fr; gap: 10px; margin-right: 0; }
}

/* ============================================================
 SECTION SUMMARY (collapsed-section meta line)
 Mirrors production FixedWidgets.vue so each section header's
 zone-count + tripwire-count pills pick up the section's accent
 colour (zones = solid border, tripwires = dashed border).
============================================================ */
.sum-pill{
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: .7rem; font-weight: 700; letter-spacing: .3px;
    text-transform: uppercase;
    line-height: 1;
    white-space: nowrap;   /* keep "3 zones" on one line */
    flex: 0 0 auto;        /* whole pill wraps as a unit instead of the number */
}
.sum-pill i{ font-size: .72rem }

.sum-zones{
    color: var(--accent, #6b7a86);
    background: color-mix(in srgb, var(--accent, #6b7a86) 10%, #fff);
    border: 1px solid color-mix(in srgb, var(--accent, #6b7a86) 35%, #e6edf3);
}

.sum-tripwire{
    color: var(--accent, #6b7a86);
    background: color-mix(in srgb, var(--accent, #6b7a86) 10%, #fff);
    border: 1px dashed color-mix(in srgb, var(--accent, #6b7a86) 55%, #fff);
}

/* Greyed-out wrapper around a real <Section> used as a teaser for zone
   types the demo isn't configured for. Matches the layout of any other
   Section in the data tab (collapsible chevron, badge, sum-pills) so
   viewers see the full customer-journey structure — but desaturated to
   a neutral grey and click-disabled.
   The underlying <section> still carries a data-section-id, so the
   sticky ZoneTypeNav's IntersectionObserver tracks scroll position
   normally when the user passes over these teasers. */

/* Override the SectionHead's --accent variable so the chevron + icon
   render in neutral grey instead of the zone-type accent color.
   The !important is required because SectionHead sets --accent inline
   via :style="{ '--accent': accent }", and inline styles otherwise win. */
.section-greyed :deep(.section-head){
    --accent: #94a3b8 !important;
    cursor: not-allowed !important;
    pointer-events: none;  /* swallow clicks on the chevron/title */
}
.section-greyed :deep(.section-head .chev),
.section-greyed :deep(.section-head .sec-icon){
    color: #94a3b8 !important;
}
.section-greyed :deep(.section-head:hover){
    background: transparent !important;
}

/* Mute the title text and the mode badge so the whole row reads as
   inactive. Sum-pills get a slightly faded look but stay readable so
   the zone/tripwire counts remain legible. */
.section-greyed :deep(.section-head h5){
    color: #94a3b8;
}
.section-greyed :deep(.section-head .mode-badge){
    opacity: 0.6;
    filter: grayscale(1);
}
.section-greyed :deep(.summary-pills){
    color: #94a3b8;
}
.section-greyed :deep(.sum-pill){
    background: #f1f5f9 !important;
    color: #94a3b8 !important;
    border-color: #e2e8f0 !important;
}
.section-greyed :deep(.sum-pill i){
    color: #94a3b8 !important;
}

/* The wrapper itself stays click-receptive (so the cursor shows over
   the whole row, not just the head), but a click on it does nothing
   since pointer-events on the inner head are off. */
.section-greyed{
    cursor: not-allowed;
}

/* Intelligence-tab two-chart row — StrategicPositioning + PatternMatrix
   side-by-side. Each chart component carries its own .sp-shell /
   .pm-shell card styling; this wrapper is just a flex layout.
   margin-bottom: 24px matches the gap below ProductZoneDeepDive's
   top KPI strip so the visual rhythm between sections is consistent. */
.intel-charts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 24px;
}
/* Zones overview: quadrant map (left) + role key (right) fills the space. */
.zones-overview {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(240px, 1fr);
  gap: 22px;
  align-items: start;
  margin-bottom: 24px;
}
.zov-chart { min-width: 0; }
.zov-roles {
  background: #fbfcfe; border: 1px solid #e8eef7; border-radius: 14px;
  padding: 14px 16px; display: flex; flex-direction: column; gap: 14px;
}
.zov-roles-title { font-size: 13px; font-weight: 800; color: #2b3a46; letter-spacing: 0.2px; }
.role-group { display: flex; flex-direction: column; gap: 5px; }
.role-head {
  font-size: 12.5px; font-weight: 800;
  display: inline-flex; align-items: center; gap: 6px;
}
.role-ico { font-size: 13px; }
.role-count {
  margin-left: 4px; font-size: 11px; font-weight: 700; color: #6b7a87;
  background: #eef2f7; border-radius: 999px; padding: 0 7px; line-height: 1.5;
}
.role-meaning { font-size: 11.5px; color: #6b7a87; line-height: 1.4; }
.role-zones { display: flex; flex-direction: column; gap: 6px; margin-top: 3px; }
.role-zonerow {
  cursor: pointer; text-align: left; width: 100%;
  background: #fff; border: 1px solid #e8eef7; border-left: 3px solid #cbd6e6;
  border-radius: 8px; padding: 6px 10px;
  display: flex; flex-direction: column; gap: 2px;
  transition: background .15s ease, box-shadow .15s ease;
}
.role-zonerow:hover { background: #f4f8fd; box-shadow: 0 2px 6px rgba(13,38,76,.08); }
.role-zonerow:hover .rz-caret { color: #7a8895; transform: translateX(1px); }
/* Active = the zone whose funnel is showing below. */
.role-zonerow.active { background: #eef4fb; box-shadow: 0 2px 8px rgba(13,38,76,.10); }
.role-zonerow.active .rz-caret { color: #4F7EB3; }
.rz-top { display: flex; align-items: center; gap: 8px; }
.rz-name { font-size: 12.5px; font-weight: 700; color: #2b3a46; line-height: 1.3; flex: 1 1 auto; min-width: 0; }
.rz-eur { font-size: 12px; font-weight: 700; color: #2c6f58; font-variant-numeric: tabular-nums; flex: 0 0 auto; }
/* Chevron — the only affordance that the row opens a funnel (no extra copy). */
.rz-caret { font-size: 11px; color: #c4ccd6; flex: 0 0 auto; transition: color .15s ease, transform .15s ease; }
/* Funnel pattern pill — the diagnosis the retired grid cards used to carry. */
.rz-pattern { display: inline-flex; align-items: center; gap: 3px; align-self: flex-start; font-size: 10.5px; font-weight: 600; padding: 1px 7px; border-radius: 999px; line-height: 1.5; margin: 1px 0; }
.rz-stat { font-size: 11px; color: #7a8895; }

@media (max-width: 900px) {
  .zones-overview { grid-template-columns: 1fr; }
}
.intel-chart-block {
  flex: 1 1 480px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.intel-chart-block > * {
  width: 100%;
  height: 100%;   /* fill the stretched block so both cards match height */
  margin-bottom: 0;
}

/* Stack the two scatter charts below 1500px (mirror of FixedWidgets). Explicit
   100% basis gives the chart a clean full width to resize into when stacked. */
@media (max-width: 1500px){
  .intel-chart-block { flex-basis: 100%; }
}

</style>